import { nextTick, markRaw } from "vue";
import { getUser, addChangeListener, getPreferredStore } from "@rei/session";
import "bezier-easing";
import prerender from "../util/prerender.mjs";
import urlSearchParams from "../util/urlSearchParams.mjs";
import { metricsGetId, metricsClick, metricsView } from "../util/genericAnalytics.mjs";
import { resolveRecsScript, initRecsOnPageLoad, getRecsPlacements, initAddToCartRecs, sendRecsClick } from "../util/recommendations.mjs";
import { resolveBVScript } from "../util/bvReviews.mjs";
import { postJSON, fetchJSON, asyncFetchJSON } from "../util/fetchLib.mjs";
import { initLogger, logger } from "../util/logger.mjs";
import { formatDate, formattedPickupEadV3 } from "../util/dates.mjs";
import { setPickupModalStore, setSavedLocation, getUserLocation, getPickupModalStore } from "../util/location.mjs";
import { handleContentstackResponse } from "../util/contentstackHelpers.mjs";
import { getProductViewEventTracking, getAddToCartEventTracking } from "../util/event-tracking.mjs";
import { initializeGladlyChat, showGladlyChat } from "../util/gladlyUtils.mjs";
import resolveKlarnaOsmScript from "../util/klarna.mjs";
import { INIT_PRODUCT_PAGE_CREATE_START, PRESELECT_FEATURED_COLOR, SET_STORYTELLING_DATA, SET_ADVICE_DATA, SET_CURATED_OUTFIT_DATA, INIT_PRODUCT_PAGE_CREATED, GET_URL_SEARCH_PARAMS, LISTEN_FOR_USER_CHANGE, HANDLE_BV_LOAD, INIT_PRODUCT_PAGE_MOUNT, GET_CART_SUMMARY, GET_CART_DATA, HANDLE_COMPONENT_LOAD_SEQUENCE, CHECK_USER, GET_ADOBE_TEST_DATA, GET_PASSIVE_LOCATION, HANDLE_SEARCH_PARAMS, SET_STORE, SET_NO_LOCATION_AVAILABLE, HANDLE_STORE_SELECT_ON_SKU_CHANGE, UPDATE_SELECTED_STORE, SET_NO_STORE_AVAILABLE, GET_SITKA_INFO, GET_UGC_IMAGES, REFRESH_KLARNA_OSM, METRICS_VIEW, RETRIEVE_INVENTORY, SETUP_POP_STATE, GET_CUSTOMER_ATTRIBUTES, CLEANUP_PRODUCT_PAGE, SET_USER_SUBSCRIPTION, UPDATE_URL_SEARCH_PARAMS, HANDLE_SKU_STORE_PARAMS, HANDLE_COLOR_PARAMS, HANDLE_SKU_SELECT_CHANGE, HANDLE_COLOR_CHANGE, METRICS_CLICK, UPDATE_VIEWED_SKUS, ADD_MEMBERSHIP_TO_CART, UPDATE_CART_LOAD_MEMBERSHIP_STATUS, SET_CART_MICROSERVICE_DATA, UPDATE_CART_COUNTER, SET_MEMBERSHIP_ADDED_TO_CART_STATUS, SET_CART_MICROSERVICE_ERROR_DATA, ADD_TO_CART_ERROR, ADD_MEMBERSHIP_AND_SKU, UPDATE_INCLUDE_MEMBERSHIP, CLOSE_MODAL, ADD_TO_CART, ADD_TO_CART_SUCCESS, ADD_TO_CART_SUCCESS_INTERSTITIAL, HANDLE_ATC_RECS_FLOW, UPDATE_CART_LOAD_STATUS, SEND_ARIA_MESSAGE, HANDLE_TERMS, HANDLE_BACKORDER, UPDATE_QUANTITY, SET_SHOW_ATC_INTERSTITIAL, SET_POP_STATE_CB, TRIGGER_PAGE_BACK, HANDLE_POP_STATE, UPDATE_CART_DATA, SET_CART_SUMMARY, OPEN_MODAL, ADD_TO_CART_PICKUP_FLOW, SET_USER_DEFINED_STORE, CHECK_FOR_NEARBY_STORES, ADD_TO_CART_BUYBOX_FLOW, SET_OVER_REQUEST_MESSAGE, FOCUS_BUYBOX_ERRORS, HANDLE_STORE_PICKUP_FLOW, HANDLE_ACCEPT_TERMS, UPDATE_ACCEPT_TERMS, UPDATE_INVENTORY, METRICS_LOW_INVENTORY, UPDATE_SHIPPING_METHOD, CLEAR_FULFILLMENT_ERROR, CLEAR_STORE_SEARCH_PROBLEMS, LIMIT_INSTOCK_STORES, UPDATE_STORES_LOADING, SET_BOPUS_ANALYTICS_LINKNAME, GET_USER_GEOLOCATION, RETRIEVE_STORE_DATA, HANDLE_STORE_SEARCH_CLICK, CLEAR_STORE_DATA, UPDATE_STORE_SEARCH_TERM, UPDATE_STORE_SEARCH_ERROR, UPDATE_STORE_SEARCH_PROBLEM_MESSAGE, UPDATE_STORE_SEARCH_WARNING, HANDLE_NO_STORE_RESULTS, SET_BY_STORE_DATA, SET_STORE_DATA_ERROR, HANDLE_GEOLOCATION_CLICK, SET_GEOLOCATION_ALLOWED, UPDATE_STORE_SEARCH_GEOLOCATION, SET_GEOLOCATION_ERROR, SET_INLINE_SHIP_LOADING, CLEAR_SHIPPING_AVAILABILITY, GET_EAD_DATA, HANDLE_PDP_RECS_FLOW, CHECK_RECS_STATUS, LOAD_SCRIPT_RECOMMENDATIONS, HANDLE_PDP_RECS_INIT, LOAD_RECS_PLACEMENTS, UPDATE_RECOMMENDATIONS_SCRIPT_STATUS, UPDATE_RECOMMENDATIONS_PLACEMENTS, HANDLE_ATC_RECS_INIT, HANDLE_RECS_CLICK, LOAD_SCRIPT_BV, UPDATE_BV_SCRIPT_STATUS, HANDLE_PRERENDER, HANDLE_WISHLIST_FLOW, SET_MODAL_OPEN, CLEAR_SELECTED_WISHLIST, UPDATE_WISHLIST_ERROR, HANDLE_LOGIN, SET_CURRENT_WISHLIST, ADD_TO_WISHLIST, HANDLE_WISHLIST_ERROR, RETRIEVE_WISHLISTS, UPDATE_WISHLISTS, UPDATE_USER, CHECK_BUYBOX_VALIDATION, UPDATE_BUYBOX_VALIDATION, GET_CONTENTSTACK_DATA, LOAD_CMS_CONTENT, UPDATE_SELECTED_PRICE, UPDATE_SELECTED_COLOR, CLEAR_COLOR_ERROR, CLEAR_SIZE_ERROR, UPDATE_HERO_IMAGE, HANDLE_NOTIFY_ME_METRICS, HANDLE_COLOR_HOVER, UPDATE_TEMP_COLOR, SET_HOVER_PRICE, HANDLE_SIZE_CHANGE, UPDATE_SELECTED_SIZE, HANDLE_SIZE_HOVER, SET_SIZE_HOVER_NAME, HANDLE_REVIEW_SUMMARY_CLICK, TOGGLE_ACCORDION_SECTION, OPEN_PREORDER_MODAL, SET_ARIA_LIVE_POLITENESS, SET_ARIA_LIVE_MESSAGE, CLEAR_SKU_ERROR, UPDATE_SELECTED_SKU, HANDLE_HERO_ZOOM_OPEN, SET_TOUCH_LIBRARY, SET_IMAGE_ZOOM_REF, SHOW_ZOOM, HANDLE_HERO_ZOOM_CLOSE, HANDLE_HERO_ZOOM_IMAGE_CHANGE, HANDLE_MC_IMAGE_CLICK, SET_CURRENT_UGC_PRODUCT, HANDLE_QUANTITY_CHANGE, CLEAR_QUANTITY_ERROR, CLEAR_MODAL_DATA, SET_MODAL_DATA, OPEN_DISCLAIMER_MODAL, HANDLE_DIVIDEND_CLICK, HANDLE_MEMBER_INFO_CLICK, INIT_GLADLY_CHAT, OPEN_GLADLY_CHAT, HANDLE_INCLUDE_MEMBERSHIP_CHANGE, CLEAR_MEMBERSHIP_ERROR, HANDLE_SHIPPING_INFO_CLICK, HANDLE_OVERSIZE_INFO_CLICK, HANDLE_BADGE_CLICK, HANDLE_NOTIFY_ME_CLICK, HANDLE_NOTIFY_ME_EMAIL_CHANGE, UPDATE_NOTIFY_ME_SUCCESS, SET_NOTIFY_ME_ERROR, CLEAR_NOTIFY_ME_ERROR, UPDATE_NOTIFY_ME_EMAIL, HANDLE_FULFILLMENT_CHANGE, HANDLE_SIZE_CHART_CLICK, HANDLE_FEET_SELECT_CHANGE, FIND_RECOMMENDED_SIZES, HANDLE_INCHES_SELECT_CHANGE, SET_HAS_EAD_NETWORK_ERROR, SET_USER_LOCATION, SET_ORDER_ISSUES, HANDLE_SHIP_EAD, HANDLE_PICKUP_EAD, UPDATE_SHIPPING_AVAILABILITY, UPDATE_SHIPPING_ADDRESS, HANDLE_UPDATE_SHIPPING_ADDRESS, SET_INLINE_SHIP_EDITOR_OPEN, SET_INLINE_SHIP_INVALID, SET_SHIP_GEO_ERROR, SET_PICKUP_MODAL_STORE_FROM_CLIENT, SET_STORE_FROM_SESSION, UPDATE_PICKUP_MODAL_STORE, SET_PRODUCT_INSIGHT, SET_UGC_IMAGES, HANDLE_UGC_IMAGE_CLICK, SET_CUSTOMER_ATTRIBUTES, LOAD_SCRIPT_KLARNA, UPDATE_KLARNA_SCRIPT_STATUS, ADD_KLARNA_EVENT_LISTENER, SET_RECS_COMPONENT, SET_MODAL_CONTAINER, UPDATE_ADOBE_TEST_DATA } from "./constants.mjs";
const actions = {
  /**
   * INIT_PRODUCT_PAGE_CREATE_START
   * initialized functionality that can occur before
   * page creation initialization. Functions must be
   * compatible with SSR requirements.
   */
  async [INIT_PRODUCT_PAGE_CREATE_START]({
    dispatch,
    commit,
    state,
    getters
  }) {
    const {
      logToConsole,
      relayErrorsToDatadog,
      useDatadog
    } = getters.appPropsMap.devToggles;
    if (!state.product.displayOptions.showSkuDropDown) dispatch(PRESELECT_FEATURED_COLOR);
    initLogger({
      useNativeConsole: logToConsole,
      relayErrors: relayErrorsToDatadog,
      useDatadog
    });
    const storytelling = await import("../data/storytelling.mjs");
    const buyingAdvice = await import("../data/buyingAdvice.mjs");
    const curatedOutfitData = await import("../data/curatedOutfitData.mjs");
    commit(SET_STORYTELLING_DATA, storytelling == null ? void 0 : storytelling.default);
    commit(SET_ADVICE_DATA, buyingAdvice == null ? void 0 : buyingAdvice.default);
    commit(SET_CURATED_OUTFIT_DATA, curatedOutfitData == null ? void 0 : curatedOutfitData.default);
  },
  /**
   * INIT_PRODUCT_PAGE_CREATED
   * initializes functionality that can occur during page creation.
   * (the CREATED hook runs before render, but after prop setup)
   * CAN include window references and should only run client-side
   */
  async [INIT_PRODUCT_PAGE_CREATED]({
    getters,
    dispatch
  }) {
    const {
      bvDeferLoad
    } = getters.appPropsMap.triageToggles;
    dispatch(GET_URL_SEARCH_PARAMS);
    dispatch(LISTEN_FOR_USER_CHANGE);
    if (bvDeferLoad) dispatch(HANDLE_BV_LOAD, getters.appPropsMap.textStrings.bvLoadTimeout);
  },
  /**
   * INIT_PRODUCT_PAGE_MOUNT
   * initializes functionality that should occur during page mount
   * this should be used for any client-side interactions
   */
  async [INIT_PRODUCT_PAGE_MOUNT]({
    state,
    dispatch,
    getters,
    commit
  }) {
    var _a, _b, _c;
    const {
      triageToggles
    } = getters.appPropsMap;
    if (state.asyncComponentsMounted.BuyBoxContainer && state.asyncComponentsMounted.MediaCenterContainer) {
      dispatch(((_b = (_a = getters.appPropsMap) == null ? void 0 : _a.launchToggles) == null ? void 0 : _b.useCartSummary) ? GET_CART_SUMMARY : GET_CART_DATA);
      dispatch(HANDLE_COMPONENT_LOAD_SEQUENCE);
      await Promise.allSettled([
        dispatch(CHECK_USER),
        dispatch(GET_ADOBE_TEST_DATA),
        // TODO update all location/store selection logic PDX-5527
        dispatch(GET_PASSIVE_LOCATION)
      ]);
      if (getters.appPropsMap.triageToggles.urlSearchParams) await dispatch(HANDLE_SEARCH_PARAMS);
      if (!((_c = state.shipping) == null ? void 0 : _c.selectedStore)) await dispatch(SET_STORE);
      if (!getters.hasShippingAddress) commit(SET_NO_LOCATION_AVAILABLE, true);
      if (getters.hasShippingAddress && getters.computedSelectedStoreId) {
        await dispatch(HANDLE_STORE_SELECT_ON_SKU_CHANGE);
      } else {
        commit(UPDATE_SELECTED_STORE, null);
        commit(SET_NO_STORE_AVAILABLE, true);
      }
      if (triageToggles.getSitka) await dispatch(GET_SITKA_INFO);
      if (triageToggles.getUgcImages) await dispatch(GET_UGC_IMAGES);
      dispatch(REFRESH_KLARNA_OSM);
      dispatch(METRICS_VIEW);
      if (getters.isSkuSelected) dispatch(RETRIEVE_INVENTORY);
      dispatch(SETUP_POP_STATE);
      if (triageToggles.getCustomerAttributes) dispatch(GET_CUSTOMER_ATTRIBUTES);
    }
  },
  /**
   * CLEANUP_PRODUCT_PAGE
   * Handles unbinding events on page destroy (Vue 2.0+)
   * or unmount (Vue 3.0+)
   */
  [CLEANUP_PRODUCT_PAGE]({
    commit,
    state
  }) {
    var _a, _b;
    if (((_a = state.userSubscription) == null ? void 0 : _a.unsubscribe) && typeof ((_b = state.userSubscription) == null ? void 0 : _b.unsubscribe) === "function") state.userSubscription.unsubscribe();
    commit(SET_USER_SUBSCRIPTION, null);
    window.removeEventListener("popstate", state.popstateCallback);
  },
  /**
   * HANDLE_COMPONENT_LOAD_SEQUENCE
   * handles the loading of components that are not part of the main page scaffold
   * for SSR, or that need to be loaded client side
   *
   * @param {Object} preselectedState: state object that allows
   *   product characteristics to be preselected
   */
  [HANDLE_COMPONENT_LOAD_SEQUENCE]({
    dispatch,
    getters,
    commit
  }, preselectedState, lg = logger) {
    const {
      bvDeferLoad
    } = getters.appPropsMap.triageToggles;
    const loadReviews = () => bvDeferLoad ? () => {
    } : dispatch(LOAD_SCRIPT_BV).then(() => {
      lg.log("component load complete - bv script");
    }).catch((err) => {
      lg.error("component load failure - bv script", err);
    });
    const loadRecs = () => dispatch(HANDLE_PDP_RECS_FLOW).then(() => import("@rei/recommendations-slider")).then((recs) => {
      if (recs == null ? void 0 : recs.default) {
        commit(SET_RECS_COMPONENT, markRaw(recs.default));
        lg.log("component load complete - recs slider");
      } else throw new Error("Recs slider never loaded");
    }).catch((err) => {
      lg.error("component load failure - recs slider", err);
    });
    const loadModal = () => new Promise((res) => {
      import("../containers/ModalContainer/index.mjs").then((modal) => {
        commit(SET_MODAL_CONTAINER, markRaw(modal.default));
        lg.log("component load success - modal");
        res();
      }).catch((err) => {
        lg.error("component load failure - modal", err);
      });
    });
    const loadKlarna = () => dispatch(LOAD_SCRIPT_KLARNA).then(() => {
      lg.log("component load complete - Klarna script");
    }).catch((err) => {
      lg.error("component load failure - Klarna script", err);
    });
    return new Promise((resolve) => {
      Promise.allSettled([loadRecs(), loadModal(), loadReviews(), loadKlarna()]).then(() => {
        lg.log("component load complete");
      }).catch((err) => {
        lg.error("component load failure - unknown", err);
      }).finally(() => resolve());
    });
  },
  /**
   * GET_URL_SEARCH_PARAMS
   * Gets the key/value pairs from the url query string and saves the object to state
   *
   * @param {Object} params: search params from current URL {param : value }
   */
  [GET_URL_SEARCH_PARAMS]({
    commit
  }) {
    const params = urlSearchParams();
    commit(UPDATE_URL_SEARCH_PARAMS, params);
  },
  /**
   * HANDLE_SEARCH_PARAMS
   * Handles preselection of product data from URL params such as sku, store, color
   *
   * @param {function():Object} getParams: utility that scrapes the current
   *   URL for search params and returns a keyed object { param: value }
   *
   * @params from getters:
   * @param {Boolean} appPropsMap.triageToggles.urlSearchParams: if true, search params will be used
   *
   */
  async [HANDLE_SEARCH_PARAMS]({
    dispatch,
    state
  }, lg = logger) {
    const {
      sku,
      store,
      color
    } = state.urlSearchParams;
    try {
      if (sku) {
        await dispatch(HANDLE_SKU_STORE_PARAMS);
      } else if (color) {
        await dispatch(HANDLE_COLOR_PARAMS);
      }
    } catch (err) {
      lg.error(`parameter error, sku: ${sku}, store: ${store}, color: ${color}`, err);
    }
  },
  async [HANDLE_SKU_STORE_PARAMS]({
    dispatch,
    getters,
    state
  }, lg = logger) {
    const {
      sku,
      store
    } = state.urlSearchParams;
    const selectedSku = state.product.bySku[sku];
    const skuIsAvailable = getters.sellableStatuses.includes(selectedSku == null ? void 0 : selectedSku.status);
    if (!skuIsAvailable) lg.warn("parameter error - invalid sku passed", {
      input: sku,
      selectedSku
    });
    if (store) await dispatch(SET_STORE);
    if (skuIsAvailable) await dispatch(HANDLE_SKU_SELECT_CHANGE, sku);
  },
  async [HANDLE_COLOR_PARAMS]({
    dispatch,
    state
  }, lg = logger) {
    var _a;
    const {
      color
    } = state.urlSearchParams;
    const {
      colors
    } = state.product;
    const fullColorObject = colors.find((clr) => clr.name === color);
    if (!fullColorObject) lg.warn("parameter error - invalid color passed", {
      color
    });
    const isNewColor = (fullColorObject == null ? void 0 : fullColorObject.code) !== ((_a = state.selectedColor) == null ? void 0 : _a.code);
    if (fullColorObject && isNewColor) {
      const price = state.product.priceByColor[fullColorObject.code];
      await dispatch(HANDLE_COLOR_CHANGE, {
        color: fullColorObject,
        price
      });
    }
  },
  /**
   * PRESELECT_FEATURED_COLOR
   * Makes sure that the featured color is automatically chosen when landing on the PDP,
   * unless already chosen by a search parameter.
   */
  [PRESELECT_FEATURED_COLOR]({
    dispatch,
    state
  }) {
    var _a, _b, _c, _d, _e;
    const colorCode = (_c = (_b = (_a = state.product.displayOptions) == null ? void 0 : _a.featuredImage) == null ? void 0 : _b.color) == null ? void 0 : _c.code;
    const color = state.product.colors.find((fullColor) => fullColor.code === colorCode);
    const useColorCode = !((_d = state.selectedColor) == null ? void 0 : _d.code) && !((_e = state.selectedSku.color) == null ? void 0 : _e.code);
    if (!!color && useColorCode) {
      const price = state.product.priceByColor[colorCode];
      dispatch(HANDLE_COLOR_CHANGE, {
        color,
        price,
        checkStore: false
      });
    }
  },
  [GET_ADOBE_TEST_DATA]({
    commit,
    getters
  }, lg = logger, win = window) {
    return new Promise((resolve, reject) => {
      var _a;
      lg.log("Adobe Test and Target attempt");
      if (!((_a = win.adobe) == null ? void 0 : _a.target)) {
        lg.warn("Adobe Test and Target not on window");
        resolve();
      } else {
        const {
          getOffer
        } = win.adobe.target;
        const start = win.performance.now();
        const success = (data) => {
          const finish = win.performance.now();
          const duration = finish - start;
          commit(UPDATE_ADOBE_TEST_DATA, data);
          lg.log("Adobe Test and Target complete", {
            start,
            finish,
            duration,
            data,
            segment: getters.adobeABTestSegment,
            activeExperiments: getters.activeAdobeExperiments
          });
          resolve();
        };
        const error = (status, err) => {
          lg.error("Adobe Test and Target error", {
            status,
            error: err
          });
          reject();
        };
        getOffer({
          mbox: "target-global-mbox",
          success,
          error
        });
      }
    }).catch((error) => {
      lg.error("Adobe test and target error", error);
    });
  },
  /**
   * Metrics Click
   * uses metricsClick from genericAnalytics.util
   *
   * @param {string} config.linkName: the linkName to be appended to site:page prefix
   * @param {object} [config.clickData]: additional data to be passed to the metrics.click handler
   * @param {object} [config.event]: event passed from the click handler, use if default action
   *   needs to be prevented while click event is sent, used with config.link
   * @param {string} [config.link]: href if element is <a>, used with config.event to
   *   complete action after default is prevented
   * @param {boolean} [config.testOverride]: for unit tests. if provided, overrides
   *   metricsClick util
   */
  [METRICS_CLICK]({
    state,
    getters
  }, config) {
    const {
      testOverride,
      event,
      link,
      linkName,
      clickData
    } = config;
    const click = testOverride || metricsClick;
    click({
      linkName: `${state.analyticsConfig.prefix}${linkName}`,
      products: getters.computedProductString,
      ...clickData
    }, {
      logOverride: getters.appPropsMap.devToggles.analyticsToConsole,
      event,
      link
    }).then((navigate) => {
      navigate();
    });
  },
  /**
   * Metrics View
   *
   * @param {object} config:
   * -- OPTIONAL --
   * @param {boolean} config.logOverride: if true, log metrics to console
   * @param {function} config.metricsView: if provided, overrides metricsView util
   */
  [METRICS_VIEW]({
    state,
    getters,
    commit
  }, testOverride) {
    var _a, _b, _c, _d, _e;
    const view = testOverride || metricsView;
    const eligibleTests = getters.getEligibleTestsList;
    const ugcImageCount = (_b = (_a = state == null ? void 0 : state.ugcImageResponse) == null ? void 0 : _a.UgcData) == null ? void 0 : _b.size;
    const clientSideAnalytics = {
      ...state.analytics,
      ...getProductViewEventTracking((_c = getters.computedSelectedSku) == null ? void 0 : _c.skuId),
      eligibleTests,
      ugcImageCount,
      pdpUGC: ugcImageCount > 0,
      pdpMemBanner: ((_d = getters.membershipBillboardProps) == null ? void 0 : _d.showAnyBillboard) ?? false
    };
    commit(UPDATE_VIEWED_SKUS, (_e = getters.computedSelectedSku) == null ? void 0 : _e.skuId);
    view(clientSideAnalytics, getters.appPropsMap.devToggles.analyticsToConsole);
  },
  async [ADD_MEMBERSHIP_TO_CART]({
    commit,
    dispatch,
    getters
  }, {
    linkName = "add-membership-to-cart"
  }, post = postJSON, lg = logger) {
    var _a;
    if (!getters.hasMembershipInCart) {
      commit(UPDATE_CART_LOAD_MEMBERSHIP_STATUS, true);
      const {
        membershipSkuId,
        membershipStyleId
      } = getters.appPropsMap.textStrings;
      const {
        useCartSummary
      } = getters.appPropsMap.launchToggles;
      const model = [{
        sku: membershipSkuId,
        quantity: 1
      }];
      try {
        lg.log("Membership add to cart ms - attempt");
        dispatch(METRICS_CLICK, {
          linkName,
          clickData: {
            products: membershipStyleId
          }
        });
        const atcResponse = await post(getters.appPropsMap.endpoints.addToCartMicroservice, model, {
          Accept: "application/json;q=0.9;v=1"
        });
        const getCartResponse = await post(getters.appPropsMap.endpoints.getCartMicroservice, null, {
          Accept: "application/json;q=0.9;v=1"
        });
        if (atcResponse.ok && getCartResponse.ok) {
          const getCartData = await getCartResponse.json();
          if (((_a = getCartData == null ? void 0 : getCartData.lineItems) == null ? void 0 : _a.length) > 0) {
            commit(SET_CART_MICROSERVICE_DATA, getCartData);
            dispatch(UPDATE_CART_COUNTER);
            commit(SET_MEMBERSHIP_ADDED_TO_CART_STATUS, true);
            lg.log("Membership add to cart ms - success");
          } else {
            throw new Error("Membership add to cart ms - unexpected cart data");
          }
        } else if (atcResponse.status === 412) {
          const atcData = await atcResponse.json();
          commit(SET_CART_MICROSERVICE_ERROR_DATA, atcData);
          dispatch(useCartSummary ? GET_CART_SUMMARY : GET_CART_DATA);
          commit(SET_MEMBERSHIP_ADDED_TO_CART_STATUS, true);
        } else {
          throw new Error("Membership add to cart ms - unknown error");
        }
      } catch (error) {
        dispatch(ADD_TO_CART_ERROR, error);
      }
      commit(UPDATE_CART_LOAD_MEMBERSHIP_STATUS, false);
    }
  },
  [ADD_MEMBERSHIP_AND_SKU]({
    commit,
    dispatch,
    state,
    getters
  }) {
    commit(UPDATE_INCLUDE_MEMBERSHIP, true);
    dispatch(METRICS_CLICK, {
      linkName: "modal_just for members_add membership to cart",
      clickData: {
        products: `;${state.product.styleId};${getters.appPropsMap.textStrings.membershipStyleId}`
      }
    });
    dispatch(CLOSE_MODAL);
    return dispatch(ADD_TO_CART);
  },
  /**
   * Show the Add to Cart Success Interstitial page
   * @param {object} getters.cartItemData Data from cart POST response
   */
  [ADD_TO_CART_SUCCESS]({
    dispatch,
    getters
  }) {
    dispatch(METRICS_CLICK, {
      linkName: "added to cart",
      clickData: {
        totalCartDemand: getters.cartItemData.subtotal,
        totalCartQuantity: getters.cartItemData.totalQuantity,
        events: "event15,scAdd"
      }
    });
    dispatch(UPDATE_CART_COUNTER, getters.cartItemData.totalQuantity);
    dispatch(ADD_TO_CART_SUCCESS_INTERSTITIAL);
    return dispatch(HANDLE_ATC_RECS_FLOW);
  },
  async [ADD_TO_CART]({
    getters,
    commit,
    dispatch
  }, post = postJSON, lg = logger) {
    var _a;
    try {
      const {
        addToCartMicroserviceModel
      } = getters;
      lg.log("Request - add to cart ms", {
        addToCartMicroserviceModel
      });
      commit(UPDATE_CART_LOAD_STATUS, true);
      dispatch(SEND_ARIA_MESSAGE, {
        message: "Add to Cart Loading",
        politeness: "assertive"
      });
      const response = await post(getters.appPropsMap.endpoints.addToCartMicroservice, addToCartMicroserviceModel, {
        Accept: "application/json;q=0.9;v=1"
      });
      if (response.status === 412) {
        return await response.json().then((data) => {
          var _a2, _b;
          commit(SET_CART_MICROSERVICE_ERROR_DATA, data);
          if (((_a2 = getters.cartErrorData) == null ? void 0 : _a2.scenario) === "terms") return dispatch(HANDLE_TERMS);
          const available = parseInt(getters.cartErrorNumberOfItemsAvailable, 10);
          if (((_b = getters.cartErrorData) == null ? void 0 : _b.scenario) === "backorder") {
            if (available > 0) return dispatch(HANDLE_BACKORDER);
            commit(SET_CART_MICROSERVICE_ERROR_DATA, {
              ...data,
              isZeroAvailability: true
            });
            commit(UPDATE_QUANTITY, available);
            return dispatch(ADD_TO_CART_ERROR, {
              value: "zero available",
              data
            });
          }
          return dispatch(ADD_TO_CART_ERROR, {
            value: "unknown add to cart ms scenario",
            data
          });
        });
      }
      if (!response.ok) {
        const responseJson = await response.json();
        commit(SET_CART_MICROSERVICE_ERROR_DATA, responseJson);
        return dispatch(ADD_TO_CART_ERROR, {
          value: "unknown add to cart ms response",
          status: responseJson.status,
          statusText: responseJson.error
        });
      }
      const cartResponse = await post(getters.appPropsMap.endpoints.getCartMicroservice, null, {
        Accept: "application/json;q=0.9;v=1"
      });
      if (!cartResponse.ok) {
        const cartResponseJson = await cartResponse.json();
        commit(SET_CART_MICROSERVICE_ERROR_DATA, cartResponseJson);
        return dispatch(ADD_TO_CART_ERROR, cartResponseJson);
      }
      const cart = await cartResponse.json();
      if (((_a = cart == null ? void 0 : cart.lineItems) == null ? void 0 : _a.length) > 0) {
        commit(SET_CART_MICROSERVICE_DATA, cart);
        return dispatch(ADD_TO_CART_SUCCESS);
      }
      commit(SET_CART_MICROSERVICE_ERROR_DATA, {});
      return dispatch(ADD_TO_CART_ERROR, {
        value: "get cart ms response data error",
        ...cart
      });
    } catch (error) {
      commit(SET_CART_MICROSERVICE_ERROR_DATA, {});
      return dispatch(ADD_TO_CART_ERROR, error);
    } finally {
      commit(UPDATE_CART_LOAD_STATUS, false);
    }
  },
  [ADD_TO_CART_SUCCESS_INTERSTITIAL]({
    commit
  }, win = window) {
    commit(SET_SHOW_ATC_INTERSTITIAL, true);
    win.scrollTo({
      top: 0
    });
    win.history.pushState({
      showAtcInterstitial: true
    }, "");
  },
  [SETUP_POP_STATE]({
    dispatch,
    commit
  }, win = window) {
    const cb = (event) => dispatch(HANDLE_POP_STATE, event);
    commit(SET_POP_STATE_CB, cb);
    win.addEventListener("popstate", cb);
    win.history.replaceState({
      showAtcInterstitial: false
    }, "");
  },
  [TRIGGER_PAGE_BACK](ctx, win = window) {
    win.history.back();
  },
  [HANDLE_POP_STATE]({
    commit,
    state
  }, event) {
    var _a, _b;
    if (state.cartData.order || ((_a = state.cartMicroserviceData) == null ? void 0 : _a.lineItems)) {
      commit(SET_SHOW_ATC_INTERSTITIAL, !!((_b = event == null ? void 0 : event.state) == null ? void 0 : _b.showAtcInterstitial));
    }
  },
  /**
   * Interface for updating the navigation header mini cart count
   */
  [UPDATE_CART_COUNTER]() {
    document.dispatchEvent(new Event("rei.gnav.cartCountChange"));
  },
  async [GET_CART_DATA]({
    getters,
    commit
  }, getFn = fetchJSON, lg = logger) {
    if (getters.appPropsMap.triageToggles.getCart) {
      try {
        lg.log("request - get cart contents");
        if (getters.computedIsMemberOnly) {
          const cart = await getFn(getters.appPropsMap.endpoints.getCart, {
            Accept: "application/json;v=2"
          });
          lg.log("success - get cart contents");
          if (cart.order) commit(UPDATE_CART_DATA, cart);
        }
      } catch (error) {
        lg.error("Fetch error: GET_CART_DATA", error);
      }
    }
  },
  async [GET_CART_SUMMARY]({
    getters,
    commit
  }, getFn = postJSON, lg = logger) {
    if (getters.appPropsMap.triageToggles.getCart) {
      try {
        lg.log("request - get cart summary");
        const response = await getFn(getters.appPropsMap.endpoints.getCartSummary, {
          includeHasMembership: true,
          includeCartItemCount: false
        }, {
          Accept: "application/json;q=0.9;v=1"
        });
        if (response.status === 404) throw new Error("404");
        if (!response.ok) throw new Error(`cart summary returned ${response.status}`);
        const cartSummary = await response.json();
        lg.log("success - get cart summary");
        commit(SET_CART_SUMMARY, cartSummary);
      } catch (error) {
        if (error.message !== "404") lg.error("Fetch error: GET_CART_SUMMARY", error);
      }
    }
  },
  /**
   * Throws an add to cart error modal.
   * Sends logger information
   * @param {object} getters.cartErrorData maps error codes to messaging
   * @param {object} getters.appPropsMap.endpoints url info stored in app props
   * @param {object} err Add to cart error
   */
  [ADD_TO_CART_ERROR]({
    getters,
    dispatch
  }, err, lg = logger) {
    const errorMessage = "add to cart error";
    if (err) lg.error(errorMessage, err);
    const errorData = getters.cartErrorData;
    const {
      scenario,
      title
    } = errorData;
    const modalProps = {
      scenario,
      loginLink: `${getters.appPropsMap.endpoints.loginPath}?toUrl=${window.location.pathname}`
    };
    return dispatch(OPEN_MODAL, {
      ariaDescribedBy: "description",
      modalContent: "AddToCartErrorModal",
      id: "add-to-cart-popup-modal",
      label: title,
      modalProps
    });
  },
  /**
    * Handles changing the store from the Bopus modal.
    *
    * @param {object} storeEntry Bopus store data for user selected store
    */
  async [ADD_TO_CART_PICKUP_FLOW]({
    state,
    dispatch,
    commit
  }, storeEntry) {
    const {
      product,
      analytics: {
        badges,
        productVideos
      }
    } = state;
    const {
      store
    } = storeEntry;
    const data = {
      linkName: "modal_find a store_change store",
      clickData: {
        pickUpInStore_storeName: store.storeName,
        pickUpInStore_storeNumber: `${store.storeId}`,
        pickUpInStore_storeFulfillmentType: storeEntry.storeFulfillmentType,
        pickUpInStore_storeFulfillmentBopusEad: storeEntry.estimatedArrivalDate,
        pickUpInStore_distanceToSelectedStore: store.distanceInMiles,
        pickUpInStore_StyleID: `${product.styleId}`,
        pickUpInStore_hasCurbside: store.enabledForCurbside,
        shippingMethodSelected: "store",
        badges,
        productVideos
      }
    };
    dispatch(CLOSE_MODAL);
    setPickupModalStore(store);
    commit(UPDATE_SELECTED_STORE, storeEntry);
    commit(SET_USER_DEFINED_STORE, {
      storeId: store.storeId,
      storeName: store.storeName,
      distanceMessage: store.distanceMessage,
      storeAddress: store.storeAddress
    });
    dispatch(CHECK_FOR_NEARBY_STORES);
    commit(SET_NO_STORE_AVAILABLE, false);
    dispatch(METRICS_CLICK, data);
  },
  [ADD_TO_CART_BUYBOX_FLOW]({
    commit,
    state,
    getters,
    dispatch
  }, atcMetricsClickLinkName) {
    var _a;
    commit(SET_OVER_REQUEST_MESSAGE, void 0);
    if (!getters.canAddToCart) {
      return dispatch(FOCUS_BUYBOX_ERRORS);
    }
    if (state.cartLoadState) return void 0;
    if (getters.computedSelectedShippingMethod === "PICKUP" && !((_a = state.shipping.selectedStore) == null ? void 0 : _a.store)) return dispatch(HANDLE_STORE_PICKUP_FLOW);
    const membershipStyleId = getters.includeMembershipInAddToCart ? `,;${getters.appPropsMap.textStrings.membershipStyleId}` : "";
    const {
      badges,
      productVideos
    } = state.analytics;
    dispatch(METRICS_CLICK, {
      linkName: atcMetricsClickLinkName || "add to cart",
      clickData: {
        isBOPUSAvailableForSku: !getters.appPropsMap.triageToggles.storePickup,
        shippingMethodSelected: getters.computedSelectedShippingMethod === "SHIP" ? "address" : "store",
        productSkus: getters.computedSelectedSku.skuId,
        products: `;${state.product.styleId}${membershipStyleId}`,
        badges,
        productVideos,
        ...getAddToCartEventTracking(getters.computedSelectedSku.skuId, state.quantity)
      }
    });
    if (membershipStyleId) commit(SET_MEMBERSHIP_ADDED_TO_CART_STATUS, true);
    return dispatch(ADD_TO_CART);
  },
  [HANDLE_TERMS]({
    dispatch,
    getters
  }, get = fetchJSON, lg = logger) {
    return get(`${getters.appPropsMap.endpoints.termsAndConditions}${getters.computedSelectedSku.skuId}`).then((terms) => {
      if (!terms.categoryTitle && !terms.text) throw new Error("missing terms and conditions content");
      dispatch(OPEN_MODAL, {
        width: "large",
        modalContent: "TermsAndConditionsModal",
        label: terms.categoryTitle,
        modalProps: {
          termsText: terms.text,
          isEBike: getters.isEBike
        }
      });
    }).catch((err) => {
      lg.error("terms and conditions error", err);
      dispatch(ADD_TO_CART_ERROR);
    });
  },
  [HANDLE_ACCEPT_TERMS]({
    dispatch,
    commit
  }) {
    dispatch(CLOSE_MODAL);
    commit(UPDATE_ACCEPT_TERMS, true);
    return dispatch(ADD_TO_CART);
  },
  [HANDLE_BACKORDER]({
    state,
    getters,
    commit,
    dispatch
  }, win = window, lg = logger) {
    const available = getters.cartErrorNumberOfItemsAvailable;
    lg.log("PDP:Backorder", {
      available,
      requested: state.quantity,
      styleId: state.product.styleId
    });
    if (getters.appPropsMap.launchToggles.usePdpBackorder && getters.cartErrorNumberOfItemsAvailable) {
      try {
        commit(SET_OVER_REQUEST_MESSAGE, {
          requested: state.quantity,
          available: getters.cartErrorNumberOfItemsAvailable
        });
        commit(UPDATE_QUANTITY, getters.cartErrorNumberOfItemsAvailable);
        dispatch(ADD_TO_CART);
      } catch (error) {
        lg.error("PDP:Backorder", error);
      }
    } else {
      win.location.href = `${getters.backorderRedirect}&fromUrl=${win.location.pathname}`;
    }
  },
  /**
   * RETRIEVE_INVENTORY
   * retrieve inventory results by sku at inventory endpoint
   * commits to state.inventory.loaded: true if load is successful
   * commits to state.inventory.skuMap: array of objects that include sku and invLevel properties
   * if previously commited, does nothing
   *
   * @param {Boolean} state.inventory.loaded: true if results have been retrieved previously
   * @param {String} state.product.styleId: style # for current product
   * @param {String} getters.appPropsMap.endpoints.skuInventoryLevels: endpoint for
   *   retrieving inventory data
   */
  async [RETRIEVE_INVENTORY]({
    dispatch,
    state,
    commit,
    getters
  }, getFn = fetchJSON, lg = logger) {
    if (!state.inventory.loaded) {
      try {
        const {
          styleId
        } = state.product;
        const endpoint = `${getters.appPropsMap.endpoints.skuInventoryLevels}?style=${styleId}`;
        const results = await getFn(endpoint);
        commit(UPDATE_INVENTORY, results || []);
      } catch (err) {
        lg.error("Fetch error -  RETRIEVE_INVENTORY", err);
      }
    }
    dispatch(METRICS_LOW_INVENTORY);
  },
  [METRICS_LOW_INVENTORY]({
    dispatch,
    getters
  }) {
    if (getters.isLowInventorySku) {
      const {
        skuId
      } = getters.computedSelectedSku;
      dispatch(METRICS_CLICK, {
        linkName: "select sku low",
        clickData: {
          products: skuId
        }
      });
    }
  },
  async [HANDLE_STORE_PICKUP_FLOW]({
    dispatch,
    commit,
    getters,
    state
  }, showInStockOnly = false, lg = logger) {
    var _a, _b;
    const hasStorePickupModalData = getters.isSkuSelected && state.quantity > 0 && getters.selectedSizeIsAvailable && getters.selectedColorIsAvailable;
    if (!hasStorePickupModalData) {
      return dispatch(FOCUS_BUYBOX_ERRORS);
    }
    commit(UPDATE_SHIPPING_METHOD, "PICKUP");
    commit(CLEAR_FULFILLMENT_ERROR);
    dispatch(CLEAR_STORE_SEARCH_PROBLEMS);
    commit(LIMIT_INSTOCK_STORES, showInStockOnly);
    dispatch(OPEN_MODAL, {
      width: "large",
      modalContent: "StorePickupModal",
      label: "Find a store near you",
      id: "pdp-bopus-modal",
      modalProps: "findInStoreModalProps"
    });
    dispatch(METRICS_CLICK, {
      linkName: "modal_find a store_open"
    });
    try {
      const {
        storePickupRestriction
      } = getters.computedSelectedSku.shippingRestrictions;
      if (storePickupRestriction) return null;
      if (!((_a = getters.transformedBlueYonderStoreData) == null ? void 0 : _a.length) && !state.storeData.isLoading) {
        commit(UPDATE_STORES_LOADING, true);
        if (!state.storeData.searchTerm) {
          commit(SET_BOPUS_ANALYTICS_LINKNAME, "modal_find a store_geolocator");
          await dispatch(GET_USER_GEOLOCATION);
        }
        await dispatch(RETRIEVE_STORE_DATA);
      }
    } catch (err) {
      commit(UPDATE_STORES_LOADING, false);
      if (!state.storeData.fulfillmentError && !((_b = state.storeData.geoLocationError) == null ? void 0 : _b.code) && err.message !== "logged") {
        lg.error("Store pickup failure - untracked geolocate or fulfillment error:", err);
      }
    }
    return null;
  },
  [HANDLE_STORE_SEARCH_CLICK]({
    state,
    dispatch,
    commit,
    getters
  }, searchTerm) {
    const cityStateZipRegex = /(^([\w\s]+)(\.{0,1})([\s\w]*),([\w\s]+)$)|(^(\d{5})$)/;
    const validSearchTerm = cityStateZipRegex.test(searchTerm.trim());
    if (state.storeData.isLoading) return null;
    commit(SET_BOPUS_ANALYTICS_LINKNAME, "modal_find a store_textbox search");
    dispatch(CLEAR_STORE_SEARCH_PROBLEMS);
    if (searchTerm.length > 0 && validSearchTerm) {
      commit(UPDATE_STORES_LOADING, true);
      commit(CLEAR_STORE_DATA);
      commit(UPDATE_STORE_SEARCH_TERM, searchTerm);
      return dispatch(RETRIEVE_STORE_DATA).catch(() => {
        commit(UPDATE_STORE_SEARCH_ERROR, true);
        commit(UPDATE_STORE_SEARCH_PROBLEM_MESSAGE, getters.getLocationsErrorMessage);
      });
    }
    commit(UPDATE_STORE_SEARCH_ERROR, true);
    commit(UPDATE_STORE_SEARCH_PROBLEM_MESSAGE, "Enter ZIP code or city, state—be sure to add the comma (example: Seattle, WA)");
    state.storePickupValidation.searchBoxEl.focus();
    return null;
  },
  [CLEAR_STORE_SEARCH_PROBLEMS]({
    commit
  }) {
    commit(UPDATE_STORE_SEARCH_WARNING, false);
    commit(UPDATE_STORE_SEARCH_ERROR, false);
    commit(UPDATE_STORE_SEARCH_PROBLEM_MESSAGE, "");
  },
  async [RETRIEVE_STORE_DATA]({
    getters,
    commit,
    dispatch
  }, sendMetrics = true, getFn = fetchJSON, lg = logger) {
    commit(CLEAR_STORE_DATA);
    dispatch(CLEAR_STORE_SEARCH_PROBLEMS);
    const {
      blueYonderEndpoint
    } = getters;
    try {
      lg.log("Request - retrieve store modal EAD data", {
        blueYonderEndpoint
      });
      const results = await getFn(blueYonderEndpoint);
      commit(UPDATE_STORES_LOADING, false);
      if (results.length === 0) dispatch(HANDLE_NO_STORE_RESULTS);
      else {
        commit(SET_BY_STORE_DATA, results);
        if (sendMetrics) {
          dispatch(METRICS_CLICK, {
            ...getters.blueYonderStoreModalAnalytics
          });
        }
      }
      return null;
    } catch (error) {
      lg.error("Store modal EAD failure - cannot retrieve store data", error);
      commit(SET_STORE_DATA_ERROR, error);
      commit(UPDATE_STORES_LOADING, false);
      throw new Error("logged");
    }
  },
  [HANDLE_NO_STORE_RESULTS]({
    state,
    commit,
    getters
  }) {
    var _a, _b;
    const noLocationsErrorMessage = getters.appPropsMap.textStrings.fulfillmentErrorNoLocations;
    commit(UPDATE_STORE_SEARCH_WARNING, true);
    commit(UPDATE_STORE_SEARCH_PROBLEM_MESSAGE, noLocationsErrorMessage);
    if (state.modalIsOpen) (_b = (_a = state.storePickupValidation) == null ? void 0 : _a.searchBoxEl) == null ? void 0 : _b.focus();
  },
  [HANDLE_GEOLOCATION_CLICK]({
    state,
    getters,
    dispatch,
    commit
  }, lg = logger) {
    if (state.storeData.isLoading) return null;
    commit(UPDATE_STORE_SEARCH_TERM, "");
    commit(CLEAR_STORE_DATA);
    commit(UPDATE_STORE_SEARCH_ERROR, false);
    commit(SET_BOPUS_ANALYTICS_LINKNAME, "modal_find a store_geolocator");
    commit(UPDATE_STORES_LOADING, true);
    return dispatch(GET_USER_GEOLOCATION).then(() => dispatch(RETRIEVE_STORE_DATA)).catch((err) => {
      var _a, _b, _c;
      commit(UPDATE_STORE_SEARCH_ERROR, true);
      commit(UPDATE_STORE_SEARCH_PROBLEM_MESSAGE, getters.getLocationsErrorMessage);
      if (((_b = (_a = state.storeData) == null ? void 0 : _a.geoLocationError) == null ? void 0 : _b.code) === 1) {
        (_c = state.storePickupValidation) == null ? void 0 : _c.searchBoxEl.focus();
      }
      if (!state.storeData.fulfillmentError && !state.storeData.geoLocationError.code && err.message !== "logged") {
        lg.error("Store pickup failure - untracked geolocate or fulfillment error:", err);
      }
      commit(UPDATE_STORES_LOADING, false);
    });
  },
  [GET_USER_GEOLOCATION]({
    state,
    commit
  }, lg = logger) {
    const getLocation = () => new Promise((resolve, reject) => {
      window.navigator.geolocation.getCurrentPosition(resolve, reject, {
        timeout: 5e3
      });
    });
    return new Promise((resolve, reject) => {
      if (typeof window.navigator !== "undefined") {
        getLocation().then(({
          coords: {
            latitude,
            longitude
          }
        }) => {
          commit(SET_GEOLOCATION_ALLOWED, true);
          commit(UPDATE_STORE_SEARCH_GEOLOCATION, {
            latitude,
            longitude
          });
          resolve();
        }).catch((err) => {
          commit(SET_GEOLOCATION_ERROR, err);
          if (state.storeData.geoLocationError.code === 1) {
            commit(SET_GEOLOCATION_ALLOWED, false);
          } else {
            lg.error("Store pickup failure - geolocation error", err);
            return reject(new Error("logged"));
          }
          return reject(err);
        });
      } else reject(new Error("navigator API not defined"));
    });
  },
  async [HANDLE_STORE_SELECT_ON_SKU_CHANGE]({
    getters,
    commit,
    dispatch
  }, lg = logger) {
    const {
      computedSelectedSku: {
        sellable
      },
      showNotifyMe
    } = getters;
    commit(CLEAR_FULFILLMENT_ERROR);
    const timeout = setTimeout(() => {
      commit(SET_INLINE_SHIP_LOADING, false);
    }, 5e3);
    try {
      commit(CLEAR_SHIPPING_AVAILABILITY);
      commit(UPDATE_SELECTED_STORE, null);
      if (sellable && !showNotifyMe) {
        commit(SET_INLINE_SHIP_LOADING, true);
        await dispatch(GET_EAD_DATA);
        dispatch(CHECK_FOR_NEARBY_STORES);
        clearTimeout(timeout);
        commit(SET_INLINE_SHIP_LOADING, false);
      }
    } catch (error) {
      lg.error("PDP:EAD:Unknown Error", error);
      commit(SET_INLINE_SHIP_LOADING, false);
      clearTimeout(timeout);
    }
  },
  async [CHECK_FOR_NEARBY_STORES]({
    commit,
    dispatch,
    state,
    getters
  }) {
    var _a, _b, _c, _d, _e;
    if (getters.appPropsMap.launchToggles.showAvailableNearby && !((_a = state.shipping.selectedStore) == null ? void 0 : _a.fulfillmentServiceLevel) !== "BOPUS") {
      commit(UPDATE_STORES_LOADING, true);
      if ((_e = (_d = (_c = (_b = state.shipping) == null ? void 0 : _b.selectedStore) == null ? void 0 : _c.store) == null ? void 0 : _d.storeAddress) == null ? void 0 : _e.zipCode) {
        commit(UPDATE_STORE_SEARCH_TERM, state.shipping.selectedStore.store.storeAddress.zipCode);
      }
      await dispatch(RETRIEVE_STORE_DATA, false);
    }
  },
  /**
   * HANDLE_PDP_RECS_FLOW
   * Handle the flow of recs script load, init, and commit of placement data
   *
   * @param lg: logging function
   */
  [HANDLE_PDP_RECS_FLOW]({
    dispatch,
    getters
  }, lg = logger) {
    return new Promise((resolve) => {
      let placements;
      let placementNames;
      dispatch(CHECK_RECS_STATUS).then(() => dispatch(LOAD_SCRIPT_RECOMMENDATIONS)).then(() => {
        const {
          primaryRecs,
          relatedRecs,
          plaRecs,
          similarItems,
          organicRecs
        } = getters.appPropsMap.recsPlacements;
        const loadPlaRecs = getters.showPlaRecs;
        const loadSimilarItems = getters.showSimilarItems;
        const loadOrganicRecs = getters.showOrganicRecs;
        placements = {
          ...loadPlaRecs && {
            plaRecs
          },
          ...loadSimilarItems && {
            similarItems
          },
          ...loadOrganicRecs && {
            organicRecs
          },
          primaryRecs,
          relatedRecs
        };
        if (!getters.appPropsMap.triageToggles.plaRecs) {
          lg.warn("PLA App Props - disabled", {
            appPropName: "frontend.triage.toggles > plaRecs"
          });
        }
        lg.log("Recs placement names", placements);
        placementNames = Object.values(placements);
      }).then(() => dispatch(HANDLE_PDP_RECS_INIT, {
        placementNames
      })).then(() => dispatch(LOAD_RECS_PLACEMENTS, {
        placements
      })).catch((error) => {
        if (error.message !== "logged") {
          lg.error("RR - 3P error - HANDLE_PDP_RECS_FLOW - unknown", error);
        }
      }).finally(() => resolve());
    });
  },
  /**
  * CHECK_RECS_STATUS
  * Check status of needed app props before beginning recs load/init
  */
  [CHECK_RECS_STATUS]({
    getters
  }, lg = logger) {
    return new Promise((resolve, reject) => {
      if (!getters.appPropsMap.triageToggles.recommendations) {
        lg.warn("RR App Props - disabled", {
          appPropName: "productPage.recommendations.enabled"
        });
        reject(new Error("logged"));
      }
      const allPlacements = getters.appPropsMap.recsPlacements;
      if (!allPlacements) {
        lg.warn("RR App Props - placements not defined", {
          appPropName: "productPage.recommendations.placements"
        });
        reject(new Error("logged"));
      } else {
        resolve();
      }
    });
  },
  /**
   * LOAD_SCRIPT_RECOMMENDATIONS
   * load the recommendations script, handle errors, dispatch init
   *
   * @param loader: Function to load and resolve the recs script
   */
  [LOAD_SCRIPT_RECOMMENDATIONS]({
    state,
    getters,
    commit
  }, loader = resolveRecsScript, lg = logger) {
    const {
      loaded
    } = state.richRelevance;
    const src = getters.appPropsMap.scriptPaths.richRelevance;
    return new Promise((resolve, reject) => {
      if (loaded) resolve();
      else if (!src) {
        lg.warn("RR script path not defined", {
          appPropName: "productPage.scriptPaths.richRelevance"
        });
        reject(new Error("logged"));
      } else {
        loader(src).then(() => {
          commit(UPDATE_RECOMMENDATIONS_SCRIPT_STATUS, {
            loaded: true
          });
          return resolve();
        }).catch(() => {
          commit(UPDATE_RECOMMENDATIONS_SCRIPT_STATUS, {
            hasError: true
          });
          return reject(new Error("logged"));
        });
      }
    });
  },
  /**
   * HANDLE_PDP_RECS_INIT
   * Initialize page context, create RR.data.JSON on window
   *
   * @param config:
   * @param {Array} config.placementNames: list of placement names ['item_page.rec_1']
   * @param config.iropl: Function to init Recommendations
   */
  [HANDLE_PDP_RECS_INIT]({
    state,
    commit
  }, config = {}) {
    return new Promise((resolve, reject) => {
      const {
        product
      } = state;
      const {
        placementNames,
        iropl = initRecsOnPageLoad
      } = config;
      const recsConfig = {
        style: product.styleId,
        name: `${product.brand.name} ${product.title}`,
        category: product.primaryCategory.canonical,
        placements: placementNames
      };
      iropl(recsConfig).then(() => resolve()).catch(() => {
        commit(UPDATE_RECOMMENDATIONS_SCRIPT_STATUS, {
          hasError: true
        });
        reject(new Error("logged"));
      });
    });
  },
  /**
   * LOAD_RECS_PLACEMENTS
   * get the placement data from RR.data.JSON and commit to state
   *
   * @param {Object} config:
   * @param {Object} config.placements: local placement names keyed to RR placement names
   * @param {Function} config.grp: replaces RR placement names with placement data from the window
   */
  [LOAD_RECS_PLACEMENTS]({
    commit
  }, config = {}) {
    return new Promise(async (resolve, reject) => {
      const {
        placements,
        grp = getRecsPlacements
      } = config;
      const keyedPlacements = await grp(placements);
      if (!keyedPlacements) {
        reject(new Error("logged"));
      }
      commit(UPDATE_RECOMMENDATIONS_PLACEMENTS, keyedPlacements);
      resolve();
    });
  },
  /**
   * HANDLE_ATC_RECS_FLOW
   *
   * @param lg: logging function
   */
  [HANDLE_ATC_RECS_FLOW]({
    state,
    dispatch,
    getters
  }, lg = logger) {
    return new Promise((resolve) => {
      const {
        loaded,
        hasError,
        src
      } = state.richRelevance;
      if (hasError === true || loaded === false) {
        lg.error("RR - 3P error - HANDLE_ATC_RECS_FLOW - previous script load error", {
          loaded,
          hasError,
          src
        });
        resolve();
      } else {
        let placements;
        dispatch(CHECK_RECS_STATUS).then(() => {
          const {
            addToCartRecs,
            altAddToCartRecs,
            curationOutfitting
          } = getters.appPropsMap.recsPlacements;
          placements = {
            curationOutfitting,
            addToCartRecs,
            altAddToCartRecs
          };
        }).then(() => dispatch(HANDLE_ATC_RECS_INIT, {
          placementNames: Object.values(placements)
        })).then(() => dispatch(LOAD_RECS_PLACEMENTS, {
          placements
        })).then(() => resolve()).catch((error) => {
          if (error.message !== "logged") {
            lg.error("RR - 3P error - HANDLE_ATC_RECS_FLOW - unknown", error);
          }
          return resolve();
        });
      }
    });
  },
  /**
   * HANDLE_ATC_RECS_INIT
   *
   * @param {Object} config:
   * @param {Array} config.placementNames: list of RR placement names ['placement_name_1']
   * @param {Function} config.atcr: RR initialization specific to Add to Cart page context
   */
  [HANDLE_ATC_RECS_INIT]({
    state,
    commit,
    getters
  }, config = {}) {
    return new Promise((resolve, reject) => {
      const {
        placementNames,
        atcr = initAddToCartRecs
      } = config;
      const recsConfig = {
        sku: getters.computedSelectedSku.skuId,
        categoryId: state.product.primaryCategory.canonical,
        placements: placementNames
      };
      atcr(recsConfig).then(() => resolve()).catch(() => {
        commit(UPDATE_RECOMMENDATIONS_SCRIPT_STATUS, {
          hasError: true
        });
        reject(new Error("logged"));
      });
    });
  },
  /**
   * HANDLE_RECS_CLICK
   * handle sending metrics and RR server click on recommendations click
   *
   * @param {Object} data: the data to be sent by the click handler
   */
  [HANDLE_RECS_CLICK]({
    getters
  }, config) {
    const {
      data,
      recsClick = sendRecsClick
    } = config;
    recsClick(data, getters.appPropsMap.devToggles.analyticsToConsole);
  },
  [HANDLE_BV_LOAD]({
    dispatch
  }, timeout = 5e3, lg = logger) {
    let loaded = false;
    const loadBV = () => {
      loaded = true;
      return dispatch(LOAD_SCRIPT_BV).then(() => {
        lg.log("component load complete - bv script");
      }).catch((err) => {
        lg.error("component load failure - bv script", err);
      });
    };
    const timeoutID = window.setTimeout(() => {
      if (loaded) return;
      lg.log("BV Load: window load timed out");
      loadBV();
    }, timeout);
    window.onload = () => {
      lg.log("Onload event fired!");
      window.clearTimeout(timeoutID);
      if (!loaded) loadBV();
    };
  },
  /**
   * LOAD_SCRIPT_BV
   * Load the BV JS script to load in the reviews and handle errors.
   *
   * @param fnOverrides.loader: Function to load and resolve the reviews script
   * @param fnOverrides.lg: logger
   */
  [LOAD_SCRIPT_BV]({
    state,
    getters,
    commit
  }, fnOverrides = {}) {
    const {
      loader = resolveBVScript,
      lg = logger
    } = fnOverrides;
    const {
      bvReviews
    } = getters.appPropsMap.triageToggles;
    const src = getters.appPropsMap.scriptPaths.bvReviews;
    const {
      loaded
    } = state.bvReviews;
    return new Promise((resolve) => {
      if (loaded) resolve();
      else if (!bvReviews) {
        lg.warn("BV App Props - disabled", {
          appPropName: "frontend.triage.toggles > bvReviews"
        });
        resolve();
      } else {
        loader(src).then(() => {
          commit(UPDATE_BV_SCRIPT_STATUS, {
            loaded: true
          });
          return resolve();
        }).catch(() => {
          commit(UPDATE_BV_SCRIPT_STATUS, {
            hasError: true
          });
          return resolve();
        });
      }
    });
  },
  /**
  * HANDLE_PRERENDER
  * Once BV Container is loaded, sets mutation observer to watch for content load,
  * then sets window/DOM elements to let prerender service know the page is rendered
  *
  * @param {Object} bvRef: the DOM node within which BV renders
  * @param {Fn} pr triggers prerender utility (sets prerender status code)
  */
  [HANDLE_PRERENDER](context, bvRef, pr = prerender, lg = logger) {
    var _a;
    if (!((_a = context.getters) == null ? void 0 : _a.showNativeReviews)) {
      const start = window.performance.now();
      const cb = (mutations, observer2) => {
        mutations.forEach((mutation) => {
          var _a2;
          if (mutation.attributeName === "data-bv-ready") {
            const isBVReady = bvRef.getAttribute("data-bv-ready");
            const hasOldBVContainer = !!document.querySelector("#BVRRContainer");
            const hasNewBVContainer = !!((_a2 = bvRef == null ? void 0 : bvRef.shadowRoot) == null ? void 0 : _a2.querySelector("#bv_review_maincontainer"));
            const hasBVContainer = hasOldBVContainer || hasNewBVContainer;
            if (isBVReady && hasBVContainer) {
              observer2.disconnect();
              pr();
              const finish = window.performance.now();
              lg.log("BV - Reviews rendered", {
                start,
                finish,
                duration: finish - start
              });
            }
          }
        });
      };
      const observer = new window.MutationObserver(cb);
      observer.observe(bvRef, {
        attributes: true
      });
    }
  },
  /** Handle Wishlist Flow
   *
   * Main action for controlling wishlist flow logic
   * https://confluence.rei.com/display/PRDT/Add+to+Wishlist+-+Spike
   * @param {object} context vuex context
   * @param {string} context.state.wishlist.selectedWishlist.id
   * @param {array} context.state.wishlist.allWishlists
   * @param {boolean} context.getters.unifiedCustomerData.isLoggedIn
   * @param {object} context.getters.computedWishlist computed data with wishlist and analytics
   * @param {function} context.commit vuex commit function
   * @param {function} context.dispatch vuex dispatch function
   * @param {object} [wishlist] if passed, gets set as current wishlist in vuex
   * @returns {promise}
   */
  [HANDLE_WISHLIST_FLOW]({
    state,
    getters,
    commit,
    dispatch
  }, wishlist = {}) {
    commit(SET_MODAL_OPEN, false);
    commit(CLEAR_SELECTED_WISHLIST);
    commit(UPDATE_WISHLIST_ERROR, {
      hasError: false,
      errorCode: ""
    });
    if (!getters.canAddToCart) return dispatch(FOCUS_BUYBOX_ERRORS);
    if (!getters.unifiedCustomerData.isLoggedIn) return dispatch(HANDLE_LOGIN);
    if (wishlist.list && wishlist.list.id) commit(SET_CURRENT_WISHLIST, wishlist);
    if (state.wishlist.selectedWishlist.list.id) {
      return dispatch(ADD_TO_WISHLIST).catch((err) => dispatch(HANDLE_WISHLIST_ERROR, err));
    }
    return dispatch(RETRIEVE_WISHLISTS).then(() => {
      if (state.wishlist.allWishlists.length === 0) {
        return dispatch(OPEN_MODAL, {
          modalContent: "AddToWishlistModal",
          label: "Oops something went wrong",
          labelIsTitle: true,
          id: "add-to-wishlist-popup-modal",
          modalProps: {
            ...getters.computedWishlist
          }
        });
      }
      if (state.wishlist.allWishlists.length === 1) {
        commit(SET_CURRENT_WISHLIST, state.wishlist.allWishlists[0]);
        return dispatch(ADD_TO_WISHLIST).catch((err) => dispatch(HANDLE_WISHLIST_ERROR, err));
      }
      return dispatch(OPEN_MODAL, {
        modalContent: "AddToWishlistModal",
        label: "Select a wish list",
        labelIsTitle: true,
        id: "add-to-wishlist-popup-modal",
        modalProps: {
          ...getters.computedWishlist,
          showLists: true
        }
      });
    }).catch((err) => {
      dispatch(HANDLE_WISHLIST_ERROR, err);
    });
  },
  /** Retrieve wishlists action. Fetches wishlists and commits to state
   *
   * @param {object} context vuex context
   * @param {*} context.commit vuex commit fn
   * @param {*} context.getters.appPropsMap getter for client side app props
   * @param {*} [getFn] override for local library fetchJSON function for testing.
   * @returns {promise} resolves with wishlists data from api
   */
  [RETRIEVE_WISHLISTS]({
    getters,
    commit
  }, get = fetchJSON) {
    const handleKnownErrors = (err, reject) => {
      const errData = typeof err.error === "string" ? {
        message: err.error,
        code: err.statusCode
      } : {
        message: err.error[0].message,
        code: err.error[0].code
      };
      reject(errData);
    };
    const isKnownError = (err) => {
      if (!err.error) return false;
      if (Array.isArray(err.error)) return true;
      if (typeof err.error === "string") return true;
      return false;
    };
    return new Promise((resolve, reject) => {
      get(getters.appPropsMap.endpoints.getWishlists.replace("?includeEntries=false", "")).then((wishlists) => {
        if (isKnownError(wishlists)) return handleKnownErrors(wishlists, reject);
        commit(UPDATE_WISHLISTS, wishlists);
        return resolve(wishlists);
      }).catch((err) => {
        if (isKnownError(err)) return handleKnownErrors(err, reject);
        return reject(err);
      });
    });
  },
  /** Add to wishlist action.
   * Takes selected wishlist item data from state, posts it to api and handles response
   * @param {object} context vuex context
   * @param {string} context.state.wishlist.selectedWishlist.list.id
   * @param {object} context.getters.computedWishlist
   * @param {object} context.getters.computedWishlist.analytics
   * @param {function} [postFn] optional override function to post data
   */
  [ADD_TO_WISHLIST]({
    state,
    getters,
    dispatch
  }, post = postJSON, lg = logger) {
    return new Promise((resolve, reject) => {
      if (!state.wishlist.selectedWishlist.list.id) reject(new Error("no wishlist selected"));
      else {
        const uri = getters.appPropsMap.endpoints.addToWishlist.replace(/:listId/, state.wishlist.selectedWishlist.list.id);
        lg.log("wishlist add attempt");
        post(uri, getters.computedWishlist.wishlistItem).then((res) => {
          if (res.status === 201) {
            dispatch(METRICS_CLICK, {
              ...getters.computedWishlist.analytics
            });
            dispatch(OPEN_MODAL, {
              label: "Success!",
              labelIsTitle: true,
              width: "medium",
              id: "add-to-wishlist-popup-modal",
              modalContent: "AddToWishlistModal",
              modalProps: {
                ...getters.computedWishlist,
                showSuccess: true
              }
            });
            dispatch(RETRIEVE_WISHLISTS);
            return resolve();
          }
          try {
            return res.json().then((body) => {
              let err;
              if (Array.isArray(body.error)) {
                err = {
                  message: body.error[0].message,
                  code: body.error[0].code
                };
              }
              if (typeof body.error === "string") {
                err = {
                  message: body.error,
                  code: body.statusCode
                };
              }
              dispatch(HANDLE_WISHLIST_ERROR, err);
              resolve();
            });
          } catch (err) {
            dispatch(HANDLE_WISHLIST_ERROR, err);
            return resolve();
          }
        }).catch((err) => reject(err));
      }
    });
  },
  /**
   *
   * @param {Object} context Vuex context
   * @param {Error | object} error Error information.
   * @param { Function } log Logger utility for testing
   * If thrown error, passed along to logging service.
   * If it's a known error response from the wishlist service, recorded in state
   */
  [HANDLE_WISHLIST_ERROR]({
    getters,
    commit,
    dispatch
  }, error = {}, lg = logger) {
    let errData = "unknown wishlist error";
    if (error.message || error.code) {
      errData = {
        value: error.message,
        code: error.code
      };
    }
    if (error instanceof Error) errData = error;
    lg.error("wishlist error", errData);
    commit(UPDATE_WISHLIST_ERROR, {
      hasError: true,
      errorCode: error.code
    });
    dispatch(OPEN_MODAL, {
      modalContent: "AddToWishlistModal",
      label: "Oops",
      labelIsTitle: true,
      id: "add-to-wishlist-popup-modal",
      modalProps: {
        ...getters.computedWishlistError,
        showError: true,
        wishlistUrl: getters.computedWishlist.wishlistUrl
      }
    });
  },
  /**
   * @param {Object} [userData] Data on the current user's status
   * @param {Function} [queryUser] Function that returns a promise with user data
   */
  async [CHECK_USER]({
    commit,
    dispatch
  }, data, queryUser = getUser, lg = logger) {
    var _a, _b;
    try {
      const userData = data || await queryUser();
      const storeId = (_a = userData.preferredStore) == null ? void 0 : _a.id;
      const user = {
        isLoggedIn: userData.isLoggedIn,
        isEmployee: !!userData.employeeId,
        isMember: !!((_b = userData.membership) == null ? void 0 : _b.memberNumber),
        preferredStore: storeId ? +storeId : null
      };
      lg.log("User Profile", {
        ...user
      });
      if (userData.isLoggedIn) dispatch(RETRIEVE_WISHLISTS);
      commit(UPDATE_USER, user);
    } catch (e) {
      lg.error(e);
    }
  },
  /**
   * @param {*} context.state.appProps Client side app props
   * @param {*} [payload] Vuex payload - not used
   * @param {object} [win] optional window object for testing
  */
  [HANDLE_LOGIN]({
    getters
  }, payload, win = window) {
    win.location.href = getters.computedLoginPath;
  },
  [CHECK_BUYBOX_VALIDATION]({
    getters,
    state,
    commit
  }) {
    const {
      color,
      membership,
      size,
      sku,
      quantity,
      pickupTile,
      shipTile
    } = state.buyBoxValidation;
    const {
      computedSelectedColor,
      computedSelectedSize,
      computedSelectedSku,
      membershipMessaging,
      selectedColorIsAvailable,
      selectedSizeIsAvailable
    } = getters;
    const errors = {
      color: {
        hasError: !computedSelectedColor.code || !selectedColorIsAvailable,
        el: color.el
      },
      membership: {
        hasError: membershipMessaging.showMemberOnlyCheckbox && !membershipMessaging.includeMembership,
        el: membership.el
      },
      size: {
        hasError: !computedSelectedSize || !selectedSizeIsAvailable,
        el: size.el
      },
      sku: {
        hasError: !computedSelectedSku.skuId,
        el: sku.el
      },
      quantity: {
        hasError: !/\d/.test(state.quantity) || state.quantity < 1,
        el: quantity.el
      },
      pickupTile: {
        hasError: getters.computedSelectedShippingMethod === "PICKUP" && !getters.hasValidFulfillment,
        el: pickupTile.el
      },
      shipTile: {
        hasError: getters.computedSelectedShippingMethod === "SHIP" && !getters.hasValidFulfillment,
        el: shipTile.el
      }
    };
    commit(UPDATE_BUYBOX_VALIDATION, errors);
  },
  /**
   * Associate validation errors recorded in state with DOM elements
   * and focus on the first one found
   *
   * @param {*} { state, dispatch } Vuex contenxt
   */
  async [FOCUS_BUYBOX_ERRORS]({
    state,
    dispatch
  }) {
    dispatch(CHECK_BUYBOX_VALIDATION);
    const errors = state.buyBoxValidation;
    const errorElName = Object.keys(errors).filter((key) => errors[key].hasError && errors[key].el)[0];
    const elToFocus = state.buyBoxValidation[errorElName] ? state.buyBoxValidation[errorElName].el : null;
    if (elToFocus && typeof elToFocus.focus === "function") {
      await nextTick();
      elToFocus.focus();
    }
  },
  /**
   * Queries the cms Document ID service to retrieve
   * data, and returns it.
   *
   * @param {object} context vuex context
   * @param {object} data Data used to query cms
   * @returns Promise that either returns cms data, or an error
   */
  async [GET_CONTENTSTACK_DATA]({
    getters
  }, {
    key
  }, fetcher = fetchJSON, contentHelper = handleContentstackResponse, lg = logger) {
    try {
      const resource = getters.appPropsMap.contentstackHashes[key];
      const response = await fetcher(`${getters.appPropsMap.endpoints.contentstack}${resource}`);
      const content = await contentHelper(response, key);
      return content;
    } catch (error) {
      lg.error(error);
      return {};
    }
  },
  /** Load cms Content into a modal
   * Takes the given Modal Data and loads an associated cms component into the modal.
   * @param {object} context vuex context
   * @param {object} modalData Data to pass to the Modal component
   */
  async [LOAD_CMS_CONTENT]({
    dispatch
  }, modalData, lg = logger) {
    try {
      const cmsContent = await dispatch(GET_CONTENTSTACK_DATA, modalData);
      const modalProps = {
        ...modalData.modalProps
      };
      modalProps.htmlContent = cmsContent.template || cmsContent.content;
      const augmentedProps = {};
      if (cmsContent.title) {
        augmentedProps.label = cmsContent.title;
        augmentedProps.labelIsTitle = true;
      }
      dispatch(OPEN_MODAL, {
        ...modalData,
        ...augmentedProps,
        modalProps
      });
    } catch (err) {
      const loggedErr = err.status && err.statusText ? {
        status: err.status,
        statusText: err.statusText
      } : err;
      lg.error("Fetch error - LOAD_CMS_CONTENT", loggedErr);
    }
  },
  /**
   * HANDLE_COLOR_CHANGE
   * handle a color change event such as selecting a color manually or via search params
   *
   * @param {string} [getters.computedSelectedSku.skuId] sku # if sku selected
   * @param {Object<string>} data.color color data to update selectedColor in state
   * @param {Object<*>} data.price: price data to update selected price ()
   * @param {Object<*>} [data.metrics] metrics data to send for click event
   */
  [HANDLE_COLOR_CHANGE]({
    getters,
    state,
    commit,
    dispatch
  }, {
    color,
    price,
    metrics,
    checkStore = true
  }) {
    let newColor;
    const isNewColor = color.displayLabel !== state.selectedColor.displayLabel;
    if (state.product.mixedPriceOptions.isPriceByColor) {
      const isNewPrice = !getters.isPriceSelected || price.price.value !== getters.computedSelectedPriceValue;
      const newPrice = isNewPrice || isNewColor ? price : {};
      newColor = isNewColor || !isNewColor && isNewPrice ? color : {};
      commit(UPDATE_SELECTED_PRICE, newPrice);
    } else {
      newColor = isNewColor ? color : {};
    }
    commit(UPDATE_SELECTED_COLOR, newColor);
    dispatch(SEND_ARIA_MESSAGE, {
      message: getters.computedSizeColorMessage,
      politeness: "assertive"
    });
    commit(CLEAR_COLOR_ERROR);
    if (state.selectedSize) commit(CLEAR_SIZE_ERROR);
    commit(UPDATE_HERO_IMAGE, {});
    commit(CLEAR_STORE_DATA);
    if (typeof window !== "undefined") {
      dispatch(RETRIEVE_INVENTORY);
      dispatch(HANDLE_NOTIFY_ME_METRICS);
      dispatch(REFRESH_KLARNA_OSM);
      if (checkStore) dispatch(HANDLE_STORE_SELECT_ON_SKU_CHANGE);
      if (metrics) {
        const {
          skuId
        } = getters.computedSelectedSku;
        const userEventView = getters.isSkuFirstView ? getProductViewEventTracking(skuId) : {};
        const clickData = {
          ...metrics,
          // add productSkus only if a sku is selected
          clickData: {
            ...skuId && {
              productSkus: skuId
            },
            ...userEventView
          }
        };
        commit(UPDATE_VIEWED_SKUS, skuId);
        dispatch(METRICS_CLICK, clickData);
      }
    }
  },
  [HANDLE_COLOR_HOVER]({
    commit
  }, {
    color = {},
    priceData = {}
  }) {
    commit(UPDATE_TEMP_COLOR, color);
    commit(SET_HOVER_PRICE, priceData);
  },
  /**
   * Handle size change event
   * @param {string} [getters.computedSelectedSku.skuId] sku # if sku selected
   * @param {String} data.newSize size string to update selectedSize in state
   * @param {Object} [data.metrics] metrics data to send for click event
   */
  [HANDLE_SIZE_CHANGE]({
    getters,
    state,
    commit,
    dispatch
  }, data) {
    const {
      newSize,
      newPrice = {}
    } = data;
    const {
      isPriceBySizeAndColor,
      isPriceBySize
    } = state.product.mixedPriceOptions;
    const isDifferentSize = newSize !== state.selectedSize;
    let size;
    let price;
    if (isPriceBySize || isPriceBySizeAndColor) {
      const isDifferentPrice = newPrice.price !== void 0 && newPrice.price.value !== getters.computedSelectedPriceValue;
      size = isDifferentSize || !isDifferentSize && isDifferentPrice ? newSize : "";
      price = isDifferentPrice || isDifferentSize ? newPrice : {};
      commit(UPDATE_SELECTED_PRICE, price);
    } else {
      size = isDifferentSize ? newSize : "";
    }
    commit(UPDATE_SELECTED_SIZE, size);
    dispatch(RETRIEVE_INVENTORY);
    dispatch(SEND_ARIA_MESSAGE, {
      message: getters.computedSizeColorMessage,
      politeness: "assertive"
    });
    commit(CLEAR_SIZE_ERROR);
    if (getters.computedSelectedColor.code) commit(CLEAR_COLOR_ERROR);
    commit(CLEAR_STORE_DATA);
    dispatch(HANDLE_STORE_SELECT_ON_SKU_CHANGE);
    dispatch(HANDLE_NOTIFY_ME_METRICS);
    dispatch(REFRESH_KLARNA_OSM);
    if (data.metrics) {
      const {
        skuId
      } = getters.computedSelectedSku;
      const userEventView = getters.isSkuFirstView ? getProductViewEventTracking(skuId) : {};
      const clickData = {
        ...data.metrics,
        // add productSkus only if a sku is selected
        clickData: {
          ...skuId && {
            productSkus: skuId
          },
          ...userEventView
        }
      };
      commit(UPDATE_VIEWED_SKUS, skuId);
      dispatch(METRICS_CLICK, clickData);
    }
  },
  [HANDLE_SIZE_HOVER]({
    commit
  }, {
    size,
    price = {}
  }) {
    commit(SET_SIZE_HOVER_NAME, size);
    commit(SET_HOVER_PRICE, price);
  },
  /**
   * HANDLE_REVIEW_SUMMARY_CLICK
   * Scrolls to review section on rating stars click
   *
   * @params from state:
   * @param {Boolean} accordionStatus.isVisible: true if the mobile accordion is visible
   * @param {Boolean} accordionStatus.reviews: true if the reviews section of
   *   the accordion is toggled open
   */
  [HANDLE_REVIEW_SUMMARY_CLICK]({
    commit,
    state
  }) {
    const scrollAnchor = document.querySelector("#reviews-scroll-anchor");
    const accordionIsVisible = state.accordionStatus.isVisible;
    const reviewsExpanded = state.accordionStatus.reviews;
    if (accordionIsVisible) {
      if (!reviewsExpanded) commit(TOGGLE_ACCORDION_SECTION, "reviews");
      const scrollAnchorButton = scrollAnchor.closest("button");
      const {
        y
      } = scrollAnchorButton.getBoundingClientRect();
      window.scrollTo(0, y - 20);
      scrollAnchorButton.focus();
    } else {
      scrollAnchor.setAttribute("tabindex", "-1");
      const {
        y
      } = scrollAnchor.getBoundingClientRect();
      window.scrollTo(0, y - 40);
      scrollAnchor.focus();
      scrollAnchor.removeAttribute("tabindex");
    }
  },
  /**
   *  open modal with preorder context and dispatch analytics
   */
  [OPEN_PREORDER_MODAL]({
    dispatch,
    getters
  }) {
    const clickData = {
      linkName: "pre order"
    };
    dispatch(OPEN_MODAL, {
      id: "preorder-modal",
      label: "About pre-order",
      labelIsTitle: true,
      modalContent: "PreorderModal",
      modalProps: {
        preOrderFaqLink: getters.appPropsMap.endpoints.preOrderFaq
      }
    });
    dispatch(METRICS_CLICK, clickData);
  },
  /**
   * Send Aria Message
   *
   * @param {string} message message to be announced in aria-live
   * @param {string} politeness 'polite' or 'assertive' as maps to aria-live attribute
   */
  [SEND_ARIA_MESSAGE]({
    commit
  }, {
    message,
    politeness = "polite"
  }) {
    commit(SET_ARIA_LIVE_POLITENESS, politeness);
    commit(SET_ARIA_LIVE_MESSAGE, message);
  },
  /**
   * Handle sku dropdown change
   *
   * @param {String} sku skuID value to update selectedSize in state
   */
  async [HANDLE_SKU_SELECT_CHANGE]({
    state,
    commit,
    dispatch,
    getters
  }, sku) {
    const newSkuObj = state.product.bySku[sku] || {
      color: {},
      size: {}
    };
    commit(CLEAR_STORE_DATA);
    commit(CLEAR_SKU_ERROR);
    commit(UPDATE_HERO_IMAGE, {});
    commit(UPDATE_SELECTED_SKU, newSkuObj);
    commit(UPDATE_SELECTED_COLOR, newSkuObj.color);
    commit(UPDATE_SELECTED_SIZE, newSkuObj.size.name);
    commit(UPDATE_SELECTED_PRICE, newSkuObj.price);
    await dispatch(HANDLE_STORE_SELECT_ON_SKU_CHANGE);
    dispatch(RETRIEVE_INVENTORY);
    dispatch(HANDLE_NOTIFY_ME_METRICS);
    dispatch(REFRESH_KLARNA_OSM);
    if (sku) {
      const {
        skuId
      } = getters.computedSelectedSku;
      const userEventView = getters.isSkuFirstView ? getProductViewEventTracking(skuId) : {};
      const clickData = {
        linkName: "select sku",
        // add productSkus only if a sku is selected
        clickData: {
          ...skuId && {
            productSkus: skuId
          },
          ...userEventView
        }
      };
      commit(UPDATE_VIEWED_SKUS, skuId);
      dispatch(METRICS_CLICK, clickData);
    }
  },
  [LISTEN_FOR_USER_CHANGE]({
    dispatch,
    commit,
    state
  }) {
    var _a, _b;
    if (((_a = state.userSubscription) == null ? void 0 : _a.unsubscribe) && typeof ((_b = state.userSubscription) == null ? void 0 : _b.unsubscribe) === "function") state.userSubscription.unsubscribe();
    const subscription = addChangeListener(async (user) => {
      var _a2, _b2;
      const isExistingUserData = user.isLoggedIn === state.user.isLoggedIn && !!((_a2 = user.membership) == null ? void 0 : _a2.memberNumber) === state.user.isMember && (((_b2 = user.preferredStore) == null ? void 0 : _b2.id) && +user.preferredStore.id) === state.user.preferredStore;
      if (!isExistingUserData) {
        dispatch(CHECK_USER, user);
        await dispatch(SET_STORE);
        dispatch(HANDLE_STORE_SELECT_ON_SKU_CHANGE);
      }
    });
    commit(SET_USER_SUBSCRIPTION, subscription);
  },
  [HANDLE_HERO_ZOOM_OPEN]({
    commit,
    state,
    dispatch
  }, {
    zoomButton,
    linkName = "product image click"
  }, lg = logger) {
    let hammerLoading;
    if (state.imageZoom.library === null) {
      lg.log("touchLib load attempt");
      hammerLoading = import("hammerjs").then((module) => {
        lg.log("touchLib load success");
        commit(SET_TOUCH_LIBRARY, module.default);
      }).catch((err) => lg.error("touchLib load error", err));
    }
    commit(SET_IMAGE_ZOOM_REF, zoomButton);
    commit(SHOW_ZOOM, true);
    dispatch(METRICS_CLICK, {
      linkName
    });
    return hammerLoading;
  },
  [HANDLE_HERO_ZOOM_CLOSE]({
    commit,
    state
  }) {
    if (state.imageZoom.buttonRef instanceof HTMLElement) state.imageZoom.buttonRef.focus();
    commit(SHOW_ZOOM, false);
  },
  [HANDLE_HERO_ZOOM_IMAGE_CHANGE]({
    dispatch,
    commit,
    getters
  }, direction) {
    const {
      computedMediaCenterImages
    } = getters;
    const currentIndex = computedMediaCenterImages.findIndex(({
      id
    }) => id === getters.computedCurrentImage.id);
    const isFirst = currentIndex === 0;
    const isLast = computedMediaCenterImages[currentIndex + 1] === void 0;
    const nextImage = isLast ? computedMediaCenterImages[0] : computedMediaCenterImages[currentIndex + 1];
    const prevImage = isFirst ? computedMediaCenterImages[computedMediaCenterImages.length - 1] : computedMediaCenterImages[currentIndex - 1];
    const image = direction === "forward" ? nextImage : prevImage;
    const newIndex = computedMediaCenterImages.findIndex(({
      id
    }) => id === image.id);
    const caption = `${newIndex + 1} of ${computedMediaCenterImages.length}`;
    dispatch(HANDLE_MC_IMAGE_CLICK, {
      image,
      caption
    });
    commit(SET_CURRENT_UGC_PRODUCT, null);
  },
  /* Compositions of synchronous mutations below */
  async [CLOSE_MODAL]({
    getters,
    dispatch,
    commit
  }, sendAnalytics = false) {
    if (sendAnalytics === true && getters.computedModalCloseLinkName) {
      dispatch(METRICS_CLICK, {
        linkName: getters.computedModalCloseLinkName
      });
    }
    commit(SET_MODAL_OPEN, false);
  },
  [HANDLE_QUANTITY_CHANGE]({
    commit,
    dispatch
  }, quantity) {
    commit(CLEAR_QUANTITY_ERROR);
    commit(UPDATE_QUANTITY, quantity);
    dispatch(HANDLE_STORE_SELECT_ON_SKU_CHANGE);
    dispatch(SEND_ARIA_MESSAGE, {
      message: `quantity set to ${quantity}`,
      politeness: "assertive"
    });
    dispatch(REFRESH_KLARNA_OSM);
  },
  [HANDLE_MC_IMAGE_CLICK]({
    commit,
    dispatch
  }, {
    image,
    caption
  }) {
    commit(UPDATE_HERO_IMAGE, image);
    dispatch(SEND_ARIA_MESSAGE, {
      message: `hero image updated to ${caption}`
    });
  },
  async [OPEN_MODAL]({
    commit
  }, modalData) {
    commit(CLEAR_MODAL_DATA);
    commit(SET_MODAL_DATA, modalData);
    await nextTick();
    commit(SET_MODAL_OPEN, true);
  },
  [OPEN_DISCLAIMER_MODAL]({
    dispatch,
    getters
  }) {
    dispatch(OPEN_MODAL, getters.disclaimerModalData);
  },
  [HANDLE_DIVIDEND_CLICK]({
    dispatch,
    getters
  }) {
    const {
      membershipCost
    } = getters.appPropsMap.textStrings;
    dispatch(OPEN_MODAL, {
      label: "The Co-op Member Reward",
      labelIsTitle: true,
      modalContent: "DividendModal",
      modalProps: {
        mastercardLink: getters.appPropsMap.endpoints.externalMastercard,
        disclaimer: getters.appPropsMap.textStrings.dividendDisclaimer,
        membershipCost
      },
      id: "dividend-popup-modal"
    });
    dispatch(METRICS_CLICK, {
      linkName: "member dividend modal"
    });
  },
  async [HANDLE_MEMBER_INFO_CLICK]({
    dispatch
  }, lg = logger) {
    let cmsContent;
    try {
      cmsContent = await dispatch(GET_CONTENTSTACK_DATA, {
        key: "membershipModal"
      });
      const {
        title,
        content
      } = cmsContent;
      dispatch(OPEN_MODAL, {
        modalContent: "HTMLContentModal",
        id: "member-popup-modal",
        labelIsTitle: true,
        label: title,
        modalProps: {
          htmlContent: content
        }
      });
      dispatch(METRICS_CLICK, {
        linkName: "lifetime membership modal"
      });
    } catch (err) {
      lg.error("Fetch error - HANDLE_MEMBER_INFO_CLICK", err);
    }
  },
  [INIT_GLADLY_CHAT]() {
    initializeGladlyChat();
  },
  [OPEN_GLADLY_CHAT]() {
    showGladlyChat();
  },
  [HANDLE_INCLUDE_MEMBERSHIP_CHANGE]({
    commit
  }, data) {
    commit(CLEAR_MEMBERSHIP_ERROR);
    commit(UPDATE_INCLUDE_MEMBERSHIP, data);
  },
  /**
   * HANDLE_SHIPPING_INFO_CLICK
   * @param {Array} getters.computedShippingRestrictions: list of compiled shipping restrictions
   */
  [HANDLE_SHIPPING_INFO_CLICK]({
    dispatch,
    state,
    getters
  }) {
    const {
      product: {
        eligibleForShipping
      }
    } = state;
    const label = eligibleForShipping ? "REI shipping terms & conditions" : "Shipping Restrictions";
    const modalData = {
      label,
      labelIsTitle: true,
      modalContent: "ShippingTermsModal",
      id: "free-shipping-popup-modal",
      key: "freeShippingModal",
      modalProps: {
        restrictions: getters.computedShippingRestrictions
      }
    };
    dispatch(eligibleForShipping ? LOAD_CMS_CONTENT : OPEN_MODAL, modalData);
    dispatch(METRICS_CLICK, {
      linkName: `free ship_${state.messages.freeShipping.message}`
    });
  },
  /**
   * HANDLE_OVERSIZE_INFO_CLICK
   * @param {Array} getters.computedShippingRestrictions: list of compiled shipping restrictions
   */
  [HANDLE_OVERSIZE_INFO_CLICK]({
    dispatch,
    state,
    getters
  }) {
    const modalData = {
      label: "Oversize Shipping",
      labelIsTitle: true,
      modalContent: "OversizeShipModal",
      id: "oversize-shipping-popup-modal",
      modalProps: {
        oversizeCharges: state.product.oversizeCharges,
        restrictions: getters.computedShippingRestrictions
      }
    };
    dispatch(OPEN_MODAL, modalData);
  },
  /**
   * HANDLE_BADGE_CLICK
   * @param {Array} getters.computedBadges: product badge object
   */
  [HANDLE_BADGE_CLICK]({
    dispatch
  }, data) {
    const modalDataNew = {
      label: data.modalTitle,
      labelIsTitle: true,
      modalContent: "HTMLContentModal",
      id: "badge-modal",
      modalProps: {
        htmlContent: data.modalContent
      }
    };
    dispatch(OPEN_MODAL, modalDataNew);
  },
  /**
   * HANDLE_NOTIFY_ME_CLICK
   * @param {String} email: an email address
   */
  async [HANDLE_NOTIFY_ME_CLICK]({
    state,
    dispatch,
    commit,
    getters
  }, email, post = postJSON) {
    var _a;
    await dispatch(HANDLE_NOTIFY_ME_EMAIL_CHANGE, email);
    if (!getters.canAddToCart) {
      dispatch(FOCUS_BUYBOX_ERRORS);
    } else if (!state.buyBoxValidation.notifyMe.hasError) {
      const sku = (_a = getters.computedSelectedSku) == null ? void 0 : _a.skuId;
      const response = await post(getters.appPropsMap.endpoints.notifyMe, {
        sku,
        email,
        skuDescription: state.product.title
      });
      if (response.ok) {
        commit(UPDATE_NOTIFY_ME_SUCCESS, sku);
        dispatch(METRICS_CLICK, {
          linkName: "notify me signup",
          clickData: {
            productSkus: sku
          }
        });
      } else {
        commit(SET_NOTIFY_ME_ERROR, "Oh no! Something went wrong. Please try again.");
      }
    }
  },
  async [HANDLE_NOTIFY_ME_EMAIL_CHANGE]({
    commit,
    dispatch,
    state
  }, email) {
    const existingError = state.buyBoxValidation.notifyMe.hasError;
    const emailRegex = /^[-a-z0-9~!$%^&*_=+}{'?]+(\.[-a-z0-9~!$%^&*_=+}{'?]+)*@([a-z0-9][-a-z0-9]*(\.[-a-z0-9]+)*\.([a-z]{2,})|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i;
    if (!emailRegex.test(email)) {
      commit(SET_NOTIFY_ME_ERROR, "Please provide a valid email address.");
      if (!existingError) dispatch(FOCUS_BUYBOX_ERRORS);
    } else {
      commit(CLEAR_NOTIFY_ME_ERROR, email);
    }
    commit(UPDATE_NOTIFY_ME_EMAIL, email);
  },
  [HANDLE_NOTIFY_ME_METRICS]({
    getters,
    dispatch
  }) {
    var _a;
    const sku = (_a = getters.computedSelectedSku) == null ? void 0 : _a.skuId;
    const shouldDispatch = getters.appPropsMap.triageToggles.notifyMe && getters.computedSelectedSku.status === "BACKORDER" && sku;
    return shouldDispatch && dispatch(METRICS_CLICK, {
      linkName: "notify me view",
      clickData: {
        productSkus: sku
      }
    });
  },
  [HANDLE_FULFILLMENT_CHANGE]({
    commit
  }, method) {
    commit(CLEAR_FULFILLMENT_ERROR);
    commit(UPDATE_SHIPPING_METHOD, method);
  },
  [HANDLE_SIZE_CHART_CLICK]({
    getters,
    dispatch
  }) {
    const kidsSizeGuideModalData = {
      width: "large",
      modalContent: "KidsSizeFinderModal",
      label: "Kids' bike size chart",
      labelIsTitle: true,
      modalProps: "kidsSizeFinderModalProps"
    };
    const sizeGuideModalData = {
      width: "large",
      modalContent: "SizeFinderModal",
      label: "Size chart",
      labelIsTitle: true,
      modalProps: "sizeFinderModalProps"
    };
    const sizeChartModalData = {
      width: "large",
      modalContent: "SizeChartModal",
      label: "Size chart",
      labelIsTitle: true,
      modalProps: {
        sizeChart: getters.sizeChartData,
        isBike: getters.isBike,
        bikeDiagram: getters.bikeDiagram,
        showSleepingBagSizeChart: getters.showSleepingBagSizeChart
      }
    };
    const sizeModalData = getters.showSizeGuide ? sizeGuideModalData : sizeChartModalData;
    const modalData = getters.isKidsBike ? kidsSizeGuideModalData : sizeModalData;
    dispatch(OPEN_MODAL, modalData);
  },
  [HANDLE_FEET_SELECT_CHANGE]({
    commit,
    dispatch
  }, data) {
    commit("UPDATE_SIZE_FINDER_FEET_SELECTED", data);
    dispatch("METRICS_CLICK", {
      linkName: "bike-size_ft"
    });
    dispatch(FIND_RECOMMENDED_SIZES);
  },
  [HANDLE_INCHES_SELECT_CHANGE]({
    commit,
    dispatch
  }, data) {
    commit("UPDATE_SIZE_FINDER_INCHES_SELECTED", data);
    dispatch("METRICS_CLICK", {
      linkName: "bike-size_in"
    });
    dispatch(FIND_RECOMMENDED_SIZES);
  },
  [FIND_RECOMMENDED_SIZES]({
    state,
    commit,
    getters,
    dispatch
  }) {
    if (getters.isHeightSelected) {
      dispatch("METRICS_CLICK", {
        linkName: "bike-size-rec-returned"
      });
      commit("UPDATE_SIZE_FINDER_RESULTS", getters.computedRecommendedSizesInches);
    }
    if (getters.isAutoSelectingBikeSizeAvailable) {
      const newBikeSizeData = {
        newSize: state.sizeFinderResults[0],
        newPrice: void 0,
        metrics: {
          linkName: "select size"
        }
      };
      dispatch(HANDLE_SIZE_CHANGE, newBikeSizeData);
    }
  },
  /**
  * 'New' EAD action that retrieve EAD data for both ship and pickup
  * TODO clean up test logic PDM-4542
  */
  async [GET_EAD_DATA]({
    state,
    getters,
    commit,
    dispatch
  }, post = postJSON, lg = logger) {
    var _a, _b;
    try {
      commit(SET_HAS_EAD_NETWORK_ERROR, false);
      commit(SET_NO_LOCATION_AVAILABLE, false);
      commit(SET_NO_STORE_AVAILABLE, false);
      const {
        shippingAddress
      } = state.shipping;
      const hasLocation = (shippingAddress == null ? void 0 : shippingAddress.postalCode) || (shippingAddress == null ? void 0 : shippingAddress.latitude) && (shippingAddress == null ? void 0 : shippingAddress.longitude);
      if (!hasLocation) {
        lg.warn("PDP:EAD", {
          message: "No user location"
        });
        commit(CLEAR_SHIPPING_AVAILABILITY);
        commit(UPDATE_SELECTED_STORE, null);
        commit(SET_NO_LOCATION_AVAILABLE, true);
        commit(SET_NO_STORE_AVAILABLE, true);
      } else {
        const sku = (_a = getters.computedSelectedSku) == null ? void 0 : _a.skuId;
        const path = `${getters.appPropsMap.endpoints.fulfillmentDeliveryDate}?validateShipMethods=true`;
        lg.log("PDP:EAD", {
          message: "fetch attempt"
        });
        const res = await post(path, getters.eadRequestPayload);
        const eads = await res.json();
        if (res.ok) {
          const dates = eads.orderLines[0];
          const address = dates.shipTo;
          const pickup = dates.fulfillmentOptions.find((opt) => {
            var _a2, _b2, _c, _d;
            return opt.fulfillmentType === "PICK" && ((_b2 = (_a2 = opt == null ? void 0 : opt.store) == null ? void 0 : _a2.storeNumber) == null ? void 0 : _b2.toString()) === ((_d = (_c = getters.computedSelectedStoreId) == null ? void 0 : _c.toString) == null ? void 0 : _d.call(_c));
          });
          const ship = dates.fulfillmentOptions.find((opt) => opt.fulfillmentServiceLevel === "STANDARD");
          const {
            shippingRestrictionMessage
          } = dates;
          dispatch(SET_USER_LOCATION, address);
          commit(SET_ORDER_ISSUES, dates.orderIssues);
          dispatch(HANDLE_SHIP_EAD, {
            ship,
            shippingRestrictionMessage
          });
          await dispatch(HANDLE_PICKUP_EAD, pickup);
          dispatch(METRICS_CLICK, {
            linkName: "ead impression",
            clickData: {
              productSkus: sku,
              isShipAtcDisabled: !!getters.outOfStockShip,
              isBopusOnly: (_b = getters.computedSelectedSku) == null ? void 0 : _b.bopusOnly
            }
          });
        } else {
          throw new Error(`Error fetching ship EADs, fetch returned ${res.status}`);
        }
      }
    } catch (error) {
      commit(CLEAR_FULFILLMENT_ERROR);
      commit(CLEAR_SHIPPING_AVAILABILITY);
      commit(UPDATE_SELECTED_STORE, null);
      commit(SET_HAS_EAD_NETWORK_ERROR, true);
      lg.error("PDP:EAD", error);
      if (getters.computedIsPickupOnly) lg.warn("PDP:EAD", {
        message: "BOPUS only"
      });
    }
  },
  /**
   * Takes shipping estimated arrival date data and formats dates for display
   * @param {*} context Vuex context
   * @param {*} shipData shipping EAD data and restrictions, if any
   * @param {*} lg logger override for test
   */
  [HANDLE_SHIP_EAD]({
    commit,
    getters
  }, {
    ship,
    shippingRestrictionMessage
  }, lg = logger) {
    var _a;
    const {
      skuId,
      status,
      bopusOnly
    } = getters.computedSelectedSku;
    if (ship) {
      const date = ship.estimatedArrivalDate && new Date(ship.estimatedArrivalDate);
      const formattedShip = {
        isAvailable: true,
        estimatedArrivalDate: date ? formatDate(date) : "",
        estimatedArrivalTimestamp: ship.estimatedArrivalDate,
        shippingRestrictionMessage
      };
      commit(UPDATE_SHIPPING_AVAILABILITY, {
        ...ship,
        ...formattedShip
      });
      lg.log("PDP:Ship EAD", {
        message: "success"
      });
    } else {
      commit(CLEAR_SHIPPING_AVAILABILITY);
      commit(UPDATE_SHIPPING_AVAILABILITY, {
        isAvailable: null,
        shippingRestrictionMessage
      });
      lg.warn("PDP:Ship EAD", {
        message: "no results",
        sku: skuId,
        status,
        bopusOnly,
        orderIssue: (_a = getters.fulfillmentOrderIssues) == null ? void 0 : _a.shipIssue
      });
    }
  },
  /**
   * receives pickup data and augments it for display. Handles side effects for UI display
   * @param {*} context Vuex context
   * @param {*} pickup store location data
   * @param {*} lg logger used for testing
   */
  async [HANDLE_PICKUP_EAD]({
    commit,
    state,
    getters
  }, pickup, lg = logger) {
    var _a;
    const {
      computedSelectedStoreId,
      computedSelectedSku
    } = getters;
    const selectedStore = ((_a = state.shipping) == null ? void 0 : _a.selectedStore) || {};
    const sku = computedSelectedSku == null ? void 0 : computedSelectedSku.skuId;
    if (!computedSelectedStoreId) {
      commit(SET_NO_STORE_AVAILABLE, true);
      lg.warn("PDP:Pickup EAD", {
        message: "no selected store"
      });
    } else if (pickup) {
      const ead = pickup.estimatedArrivalDate;
      const timeZone = pickup.store.timeZoneId;
      const localizedEad = ead && new Date(new Date(ead).toLocaleString("en-US", {
        timeZone
      }));
      if (!(selectedStore == null ? void 0 : selectedStore.store)) {
        const pickupStore = {
          storeName: pickup.store.storeDisplayName,
          storeId: pickup.store.storeNumber,
          phoneNumber: pickup.store.phone,
          distanceInMiles: pickup.store.distance,
          timeZone: pickup.store.timeZoneId,
          storeAddress: {
            zipCode: pickup.store.zip
          }
        };
        selectedStore.store = pickupStore;
      }
      selectedStore.eligibleForPickUp = true;
      selectedStore.inventoryStatus = "AVAILABLE";
      selectedStore.storeFulfillmentType = pickup.fulfillmentServiceLevel;
      selectedStore.estimatedArrivalDate = ead ? formattedPickupEadV3(localizedEad, timeZone) : "Pickup date displays in cart";
      selectedStore.estimatedArrivalTimestamp = ead;
      commit(UPDATE_SELECTED_STORE, selectedStore);
      commit(SET_NO_STORE_AVAILABLE, false);
      lg.log("PDP:Pickup EAD", {
        message: "success"
      });
    } else {
      commit(UPDATE_SELECTED_STORE, {
        store: {
          ...state.userSelectedStoreData
        },
        eligibleForPickUp: false,
        estimatedArrivalDate: "Out of stock",
        estimatedArrivalTimestamp: null,
        inventoryStatus: "NOT_AVAILABLE",
        storeFulfillmentType: "UNFULFILLABLE"
      });
      lg.warn("PDP:Pickup EAD", {
        message: "No results",
        sku,
        selectedStoreId: selectedStore.storeId
      });
      if (getters.computedIsPickupOnly) lg.warn("PDP:Pickup EAD", {
        message: "BOPUS only"
      });
    }
  },
  /**
   * Saves location data to localstorage and vuex
   * @param {*} context Vuex context
   * @param {*} location full or partial location data for ship to home
   * @param {*} saveLocationToSession override for test
   */
  [SET_USER_LOCATION]({
    commit
  }, location, saveLocationToSession = setSavedLocation) {
    const address = {
      countryCode: "",
      state: "",
      city: "",
      postalCode: "",
      latitude: "",
      longitude: "",
      ...location
    };
    saveLocationToSession(address);
    commit(UPDATE_SHIPPING_ADDRESS, address);
  },
  async [HANDLE_UPDATE_SHIPPING_ADDRESS]({
    commit,
    dispatch,
    state,
    getters
  }, {
    newAddress,
    geo
  }, lg = logger) {
    var _a;
    dispatch(METRICS_CLICK, {
      linkName: "edit ship to zip code"
    });
    commit(SET_INLINE_SHIP_EDITOR_OPEN, false);
    commit(SET_INLINE_SHIP_INVALID, false);
    commit(SET_SHIP_GEO_ERROR, false);
    commit(SET_INLINE_SHIP_LOADING, true);
    const timeoutID = window.setTimeout(() => {
      commit(SET_INLINE_SHIP_LOADING, false);
    }, 1e4);
    if (geo) {
      try {
        await dispatch(GET_USER_GEOLOCATION);
        const {
          latitude,
          longitude
        } = state.storeData.geoLocation;
        dispatch(SET_USER_LOCATION, {
          latitude,
          longitude
        });
        commit(SET_NO_LOCATION_AVAILABLE, false);
      } catch (error) {
        lg.error("PDP:Update ship address", error);
        clearTimeout(timeoutID);
        commit(SET_INLINE_SHIP_LOADING, false);
        commit(SET_INLINE_SHIP_EDITOR_OPEN, true);
        commit(SET_SHIP_GEO_ERROR, true);
        return;
      }
    } else {
      const isValid = /^\d{5}$/.test(newAddress);
      if (!isValid) {
        clearTimeout(timeoutID);
        commit(SET_INLINE_SHIP_INVALID, true);
        commit(SET_INLINE_SHIP_LOADING, false);
        commit(SET_INLINE_SHIP_EDITOR_OPEN, true);
        return;
      }
      commit(SET_NO_LOCATION_AVAILABLE, false);
      dispatch(SET_USER_LOCATION, {
        postalCode: newAddress
      });
    }
    if ((_a = getters.computedSelectedSku) == null ? void 0 : _a.sellable) await dispatch(GET_EAD_DATA);
    clearTimeout(timeoutID);
    commit(SET_INLINE_SHIP_LOADING, false);
  },
  /**
   * Checks if location has been saved to state. If not, tries to updated it with utility
   * method that looks for localstorage location data, then browser geolocation, and
   * finally Akamai cookie location
   */
  async [GET_PASSIVE_LOCATION]({
    state,
    dispatch
  }, getLocation = getUserLocation) {
    var _a, _b, _c;
    const hasLocation = ((_a = state.shipping.shippingAddress) == null ? void 0 : _a.postalCode) || ((_b = state.shipping.shippingAddress) == null ? void 0 : _b.latitude) && ((_c = state.shipping.shippingAddress) == null ? void 0 : _c.longitude);
    const location = hasLocation ? state.shipping.shippingAddress : await getLocation();
    if (location) {
      dispatch(SET_USER_LOCATION, location);
    }
  },
  /**
   * Set pickup modal store from the browser if user previously selected one,
   * otherwise get preferred store from @rei/session library
   */
  async [SET_STORE]({
    state,
    dispatch
  }) {
    await dispatch(SET_PICKUP_MODAL_STORE_FROM_CLIENT);
    if (!state.isPickupModalStoreSet) {
      await dispatch(SET_STORE_FROM_SESSION);
    }
  },
  /**
   * Get details of the user preferred store from session data and format for inline display
   * @param {*} getStore optional param for unit test override
   */
  async [SET_STORE_FROM_SESSION]({
    commit
  }, getStore = getPreferredStore) {
    const store = await getStore();
    let selectedStore = null;
    if (store) {
      selectedStore = {
        store: {
          storeId: store.storeNumber,
          specialStoreHours: [],
          curbsideHours: [],
          phoneNumber: store.phone,
          distanceMessage: null,
          distanceInMiles: store.distance,
          storeType: store.storeType,
          curbsidePhone: store.curbsidePhone,
          enabledForCurbside: store.enabledForCurbside,
          enabledForInStore: store.enabledForInStore,
          enabledForRspu: store.enabledForRSPU,
          curbsideDedicatedStalls: store.curbsideDedicatedStalls,
          curbsideParkingInstructions: store.curbsideParkingInstructions,
          storeAddress: {
            line1: store.address1,
            line2: store.address2,
            line3: store.address3,
            city: store.city,
            state: store.state,
            zipCode: store.zip,
            country: store.country
          },
          storeTimeZone: store.timeZoneId,
          storeName: store.storeDisplayName,
          storeHours: []
        },
        backOrdered: false
      };
      commit(SET_USER_DEFINED_STORE, {
        storeId: selectedStore.store.storeId,
        storeName: selectedStore.store.storeName,
        distanceMessage: selectedStore.store.distanceInMiles,
        storeAddress: selectedStore.store.storeAddress
      });
    }
    commit(UPDATE_SELECTED_STORE, selectedStore);
  },
  /**
   * Get details of the pickup modal store from browser session data and format for inline display
   * @param {*} getStore optional param for unit test override
   */
  async [SET_PICKUP_MODAL_STORE_FROM_CLIENT]({
    state,
    getters,
    commit
  }, getStoreFromClient = getPickupModalStore, getFn = fetchJSON, lg = logger) {
    let selectedStore = null;
    let pickUpModalStore;
    const {
      store
    } = state.urlSearchParams;
    if (store) {
      try {
        pickUpModalStore = await getFn(getters.appPropsMap.endpoints.getStore.replace(/:storeId/, store));
      } catch (error) {
        lg.error(`parameter error - invalid store passed: ${store}`, store);
      }
    } else {
      pickUpModalStore = await getStoreFromClient();
    }
    if (pickUpModalStore) {
      const isLegacyStoreData = !!pickUpModalStore.storeId;
      if (!isLegacyStoreData) {
        selectedStore = {
          // transform for compatibility with legacy store data
          store: {
            storeId: pickUpModalStore.storeNumber,
            specialStoreHours: [],
            curbsideHours: [],
            phoneNumber: pickUpModalStore.phone,
            distanceMessage: null,
            distanceInMiles: pickUpModalStore.distance,
            storeType: pickUpModalStore.storeType,
            curbsidePhone: pickUpModalStore.curbsidePhone,
            enabledForCurbside: pickUpModalStore.enabledForCurbside,
            enabledForInStore: pickUpModalStore.enabledForInStore,
            enabledForRspu: pickUpModalStore.enabledForRSPU,
            curbsideDedicatedStalls: pickUpModalStore.curbsideDedicatedStalls,
            curbsideParkingInstructions: pickUpModalStore.curbsideParkingInstructions,
            storeAddress: {
              line1: pickUpModalStore.address1,
              line2: pickUpModalStore.address2,
              line3: pickUpModalStore.address3,
              city: pickUpModalStore.city,
              state: pickUpModalStore.state,
              zipCode: pickUpModalStore.zip,
              country: pickUpModalStore.country
            },
            storeTimeZone: pickUpModalStore.timeZoneId,
            storeName: pickUpModalStore.storeDisplayName,
            storeHours: pickUpModalStore.hours
          },
          backOrdered: false
        };
      } else {
        selectedStore = {
          store: {
            ...pickUpModalStore
          },
          backOrdered: false
        };
      }
      commit(SET_USER_DEFINED_STORE, {
        storeId: selectedStore.store.storeId,
        storeName: selectedStore.store.storeName,
        distanceMessage: selectedStore.store.distanceInMiles,
        storeAddress: selectedStore.store.storeAddress
      });
      commit(UPDATE_PICKUP_MODAL_STORE, !!pickUpModalStore);
      setPickupModalStore(pickUpModalStore);
    }
    commit(UPDATE_SELECTED_STORE, selectedStore);
  },
  async [GET_SITKA_INFO]({
    state,
    getters,
    commit
  }, lg = logger) {
    lg.log("PDP:SITKA", {
      message: "fetch attempt"
    });
    if (state.product.styleId && !getters.isPup) {
      try {
        const shopperInsights = await fetchJSON(`${getters.appPropsMap.endpoints.shopperInsights}/${state.product.styleId}`, {
          Accept: "application/json;v=2"
        });
        lg.log("PDP:SITKA", {
          message: "fetch success"
        });
        commit(SET_PRODUCT_INSIGHT, shopperInsights.SitkaData);
      } catch (error) {
        lg.error("PDP:SITKA", error);
      }
    }
  },
  async [GET_UGC_IMAGES]({
    state,
    getters,
    commit
  }, lg = logger) {
    lg.log("PDP:UGC", {
      message: "fetch attempt"
    });
    try {
      const results = await fetchJSON(`${getters.appPropsMap.endpoints.ugcImageData}/${state.product.styleId}`);
      lg.log("PDP:UGC", {
        message: "fetch success",
        count: results == null ? void 0 : results.size
      });
      commit(SET_UGC_IMAGES, results);
    } catch (error) {
      lg.error("PDP:UGC", error);
    }
  },
  [HANDLE_UGC_IMAGE_CLICK]({
    commit,
    dispatch
  }, data) {
    commit(SET_CURRENT_UGC_PRODUCT, data.product);
    dispatch(HANDLE_HERO_ZOOM_OPEN, {
      zoomButton: data.el,
      linkName: "product image click ugc"
    });
  },
  async [GET_CUSTOMER_ATTRIBUTES]({
    commit,
    getters
  }, get = asyncFetchJSON, lg = logger, getId = metricsGetId) {
    const userAttributes = {
      member: false,
      reiccholder: false
    };
    try {
      lg.log("PDP:get customer attributes", {
        message: "fetch attempt"
      });
      const mcvId = await getId();
      if (mcvId) {
        const customerUrl = `${getters.appPropsMap.endpoints.customerAttributes}${mcvId}`;
        const {
          ok,
          json
        } = await get(customerUrl);
        if (ok) {
          userAttributes.member = (json == null ? void 0 : json.member) === true;
          userAttributes.reiccholder = (json == null ? void 0 : json.reiccholder) === true;
        }
        lg.log("PDP:get customer attributes", {
          message: "fetch success",
          userAttributes
        });
      }
    } catch (e) {
      lg.error("PDP:get customer attributes", e);
    }
    commit(SET_CUSTOMER_ATTRIBUTES, userAttributes);
  },
  /**
  * LOAD_SCRIPT_KLARNA
  * Load the Klarna JS script to display payment message.
  *
  * @param fnOverrides.loader: Function to load and resolve the script
  * @param fnOverrides.lg: logger
  */
  [LOAD_SCRIPT_KLARNA]({
    state,
    getters,
    commit,
    dispatch
  }, fnOverrides = {}, win = window) {
    const {
      loader = resolveKlarnaOsmScript,
      lg = logger
    } = fnOverrides;
    const src = getters.appPropsMap.scriptPaths.klarna;
    const {
      loaded
    } = state.klarna;
    const {
      enableKlarna
    } = getters.appPropsMap.triageToggles;
    return new Promise((resolve) => {
      if (loaded || !enableKlarna) resolve();
      else if (!src) {
        lg.warn("Klarna script path not defined", {
          appPropName: "frontend.script.paths.klarna"
        });
        resolve();
      } else {
        loader(src, {
          "data-environment": getters.appPropsMap.apiKeys.klarnaEnvironment,
          "data-client-id": getters.appPropsMap.apiKeys.klarna
        }).then(() => {
          let attempts = 0;
          const attempter = setInterval(() => {
            attempts++;
            if (attempts === 20) {
              clearInterval(attempter);
              resolve();
            }
            if (win.Klarna.OnsiteMessaging) {
              commit(UPDATE_KLARNA_SCRIPT_STATUS, {
                loaded: true
              });
              dispatch(ADD_KLARNA_EVENT_LISTENER);
              clearInterval(attempter);
              resolve();
            }
          }, 300);
        }).catch(() => {
          commit(UPDATE_KLARNA_SCRIPT_STATUS, {
            hasError: true
          });
          return resolve();
        });
      }
    });
  },
  async [REFRESH_KLARNA_OSM]({
    state
  }, win = window) {
    var _a, _b;
    if (typeof win !== "undefined" && state.klarna.loaded) {
      await nextTick();
      (_b = (_a = win.Klarna) == null ? void 0 : _a.OnsiteMessaging) == null ? void 0 : _b.refresh();
    }
  },
  /**
   * Register the event listener for modalOpened when the Klarna 'Learn More' button is clicked
   */
  [ADD_KLARNA_EVENT_LISTENER]({
    state,
    dispatch
  }, win = window) {
    const dispatchMetricsClick = () => {
      dispatch(METRICS_CLICK, {
        linkName: "klarna_learn more"
      });
    };
    if (typeof win !== "undefined" && state.klarna.loaded) {
      win.Klarna.OnsiteMessaging.on("informationalModalOpened", dispatchMetricsClick);
    }
  }
};
export {
  actions as default
};
//# sourceMappingURL=actions.mjs.map
